window.theme = window.theme || {};
window.Shopify = window.Shopify || {};

theme.config = {
  hasSessionStorage: true,
  hasLocalStorage: true,
  isTouch: 'ontouchstart' in window || navigator.MaxTouchPoints > 0 || navigator.msMaxTouchPoints > 0,
  mediaQuerySmall: '(max-width: 768px)',
};

/* -------------------------------------------------------------------------- */
/* Gsap */
/* -------------------------------------------------------------------------- */

const gsapConfig = {
  scroll: null,
  transitionOffset: 1100,
  staggerDefault: 0.07,
  durationDefault: 0.735,
  durationtSmooth: 1.47,
  durationtFaster: 0.435,
};

function initializeGsap() {
  requestAnimationFrame(() => {
    if (typeof gsap !== 'undefined' && typeof DrawSVGPlugin !== 'undefined') {
      try {
        gsap.registerPlugin(DrawSVGPlugin);
      } catch (_error) {}
    }
  });
}
initializeGsap();

/* -------------------------------------------------------------------------- */
/* Suport passive */
/* -------------------------------------------------------------------------- */

theme.supportsPassive = false;

try {
  const opts = Object.defineProperty({}, 'passive', {
    get: function () {
      theme.supportsPassive = true;
    },
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
} catch (e) {
  console.warn('Navegador não suporta eventos passive');
}

document.documentElement.classList.add(theme.config.isTouch ? 'touch' : 'no-touch');

/* -------------------------------------------------------------------------- */
/* Dom */
/* -------------------------------------------------------------------------- */

(function () {
  theme.DOMready = function (callback) {
    if (document.readyState !== 'loading') callback();
    else document.addEventListener('DOMContentLoaded', callback, {once: true});
  };

  theme.lenis = {
    lenis: null,
    config: {
      duration: 0.835,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      infinite: false,
    },
    init(customConfig = {}) {
      if (typeof Lenis === 'undefined') return;
      const config = {...this.config, ...customConfig};
      this.lenis = new Lenis({
        wrapper: window,
        content: document.documentElement,
        easing: config.easing,
        duration: config.duration,
        smoothWheel: config.smoothWheel,
        infinite: config.infinite,
      });
      this.lenis.on('scroll', (e) => theme.pubsub.publish('lenisScroll', e));
      this.startRAF();
    },
    startRAF() {
      const animate = (time) => {
        requestAnimationFrame(() => {
          this.lenis?.raf?.(time);
        });
        requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    },
  };
  document.addEventListener('DOMContentLoaded', () => theme.lenis.init());

  theme.a11y = {
    trapFocusHandlers: {},
    getFocusableElements: (container) =>
      Array.from(container.querySelectorAll('summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe')),
    trapFocus: (container, elementToFocus = container) => {
      const elements = theme.a11y.getFocusableElements(container);
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      theme.a11y.removeTrapFocus();
      const keydownHandler = (event) => {
        if (event.code.toUpperCase() !== 'TAB') return;
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          requestAnimationFrame(() => first.focus());
        }
        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          requestAnimationFrame(() => last.focus());
        }
      };
      const focusinHandler = (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) return;
        document.addEventListener('keydown', keydownHandler);
      };
      const focusoutHandler = () => {
        document.removeEventListener('keydown', keydownHandler);
      };
      theme.a11y.trapFocusHandlers.keydown = keydownHandler;
      theme.a11y.trapFocusHandlers.focusin = focusinHandler;
      theme.a11y.trapFocusHandlers.focusout = focusoutHandler;
      document.addEventListener('focusin', focusinHandler);
      document.addEventListener('focusout', focusoutHandler);
      requestAnimationFrame(() => {
        elementToFocus.focus();
        if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
          elementToFocus.setSelectionRange(0, elementToFocus.value.length);
        }
      });
    },
    removeTrapFocus: (elementToFocus = null) => {
      document.removeEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);
      document.removeEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
      document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        requestAnimationFrame(() => elementToFocus.focus());
      }
    },
  };

  theme.utils = {
    throttle: (callback, wait = 100) => {
      let lastTime = 0,
        requestId = null,
        lastArgs;
      const throttled = function (...args) {
        const now = Date.now();
        lastArgs = args;
        if (now - lastTime >= wait) {
          requestAnimationFrame(() => {
            callback.apply(this, lastArgs);
            lastTime = Date.now();
          });
        } else if (!requestId) {
          requestId = setTimeout(
            () => {
              requestAnimationFrame(() => {
                callback.apply(this, lastArgs);
                lastTime = Date.now();
                requestId = null;
              });
            },
            wait - (now - lastTime)
          );
        }
      };
      throttled.cancel = () => {
        if (requestId) clearTimeout(requestId);
        requestId = null;
      };
      return throttled;
    },
    debounce: (fn, wait) => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          requestAnimationFrame(() => fn.apply(this, args));
        }, wait);
      };
    },
    waitForEvent: (element, eventName) => {
      return new Promise((resolve) => {
        const done = (event) => {
          if (event.target === element) {
            element.removeEventListener(eventName, done);
            resolve(event);
          }
        };
        element.addEventListener(eventName, done);
      });
    },
    fetchConfig: (type = 'json') => ({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${type}`,
      },
    }),
    postLink: (path, options = {}) => {
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};
      const form = document.createElement('form');
      form.method = method;
      form.action = path;
      for (const key in params) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = key;
        hiddenField.value = params[key];
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },
    postLink2: (path, options = {}) => {
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};
      const form = document.createElement('form');
      form.method = method;
      form.action = path;
      for (const key in params) {
        for (const index in params[key]) {
          for (const key2 in params[key][index]) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = `${key}[${index}][${key2}]`;
            hiddenField.value = params[key][index][key2];
            form.appendChild(hiddenField);
          }
        }
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },
    sectionId: (element) => element.getAttribute('data-section-id'),
    imageLoaded: (imageOrArray) => {
      if (!imageOrArray) return Promise.resolve();
      const arr = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
      return Promise.all(
        arr.map(
          (image) =>
            new Promise((resolve) => {
              if ((image.tagName === 'IMG' && image.complete) || !image.offsetParent) {
                resolve();
              } else {
                image.onload = () => resolve();
              }
            })
        )
      );
    },
    visibleMedia: (media) => {
      return Array.from(media).find((item) => window.getComputedStyle(item).display !== 'none');
    },
    formatMoney: (cents, format = '') => {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || window.themeVariables.settings.moneyFormat;
      function defaultTo(value2, defaultValue) {
        return value2 == null || value2 !== value2 ? defaultValue : value2;
      }
      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ',');
        decimal = defaultTo(decimal, '.');
        if (isNaN(number) || number == null) return 0;
        number = (number / 100).toFixed(precision);
        let parts = number.split('.'),
          dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
          centsAmount = parts[1] ? decimal + parts[1] : '';
        return dollarsAmount + centsAmount;
      }
      let value = '';
      switch ((formatString.match(placeholderRegex) || [])[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', '.');
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_no_decimals_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 0, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
          break;
      }
      return formatString.replace(placeholderRegex, value);
    },
  };

  theme.HTMLUpdateUtility = {
    viewTransition: (oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) => {
      preProcessCallbacks?.forEach((cb) => cb(newContent));
      const doTransition = () => {
        const newNodeWrapper = document.createElement('div');
        theme.HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
        const newNode = newNodeWrapper.firstChild;
        const uniqueKey = Date.now();
        oldNode.querySelectorAll('[id], [form]').forEach((el) => {
          if (el.id) el.id = `${el.id}-${uniqueKey}`;
          if (el.form) el.setAttribute('form', `${el.form.getAttribute('id')}-${uniqueKey}`);
        });
        oldNode.parentNode.replaceChild(newNode, oldNode);
        postProcessCallbacks?.forEach((cb) => cb(newNode));
      };

      if (document.startViewTransition) {
        document.startViewTransition(() => {
          requestAnimationFrame(doTransition);
        });
      } else {
        requestAnimationFrame(() => {
          const newNodeWrapper = document.createElement('div');
          theme.HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
          const newNode = newNodeWrapper.firstChild;
          const uniqueKey = Date.now();
          oldNode.querySelectorAll('[id], [form]').forEach((el) => {
            if (el.id) el.id = `${el.id}-${uniqueKey}`;
            if (el.form) el.setAttribute('form', `${el.form.getAttribute('id')}-${uniqueKey}`);
          });
          oldNode.parentNode.insertBefore(newNode, oldNode);
          oldNode.style.display = 'none';
          postProcessCallbacks?.forEach((cb) => cb(newNode));
          setTimeout(() => oldNode.remove(), 500);
        });
      }
    },
    setInnerHTML: (element, html) => {
      element.innerHTML = html;
      element.querySelectorAll('script').forEach((oldScript) => {
        const newScript = document.createElement('script');
        Array.from(oldScript.attributes).forEach((attr) => {
          newScript.setAttribute(attr.name, attr.value);
        });
        newScript.appendChild(document.createTextNode(oldScript.innerHTML));
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });
    },
  };

  theme.pubsub = {
    PUB_SUB_EVENTS: {
      cartUpdate: 'cartUpdate',
      quantityUpdate: 'quantityUpdate',
      variantChange: 'variantChange',
      cartError: 'cartError',
      facetUpdate: 'facetUpdate',
      quantityRules: 'quantityRules',
      quantityBoundries: 'quantityBoundries',
      optionValueSelectionChange: 'optionValueSelectionChange',
    },
    subscribers: {},
    subscribe(eventName, callback) {
      if (!this.subscribers[eventName]) this.subscribers[eventName] = [];
      this.subscribers[eventName].push(callback);
      return () => this.unsubscribe(eventName, callback);
    },
    unsubscribe(eventName, callback) {
      if (!this.subscribers[eventName]) return;
      this.subscribers[eventName] = this.subscribers[eventName].filter((cb) => cb !== callback);
    },
    publish(eventName, data) {
      if (!this.subscribers[eventName]) return;
      requestAnimationFrame(() => {
        [...this.subscribers[eventName]].forEach((cb) => {
          try {
            cb(data);
          } catch (e) {}
        });
      });
    },
  };

  theme.cookiesEnabled = function () {
    let cookieEnabled = navigator.cookieEnabled;
    if (!cookieEnabled) {
      document.cookie = 'testcookie';
      cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
      requestAnimationFrame(() => {
        document.cookie = 'testcookie=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      });
    }
    return cookieEnabled;
  };

  theme.isStorageSupported = function (type) {
    if (window.self !== window.top) return false;
    const testKey = 'test';
    let storage = type === 'session' ? window.sessionStorage : window.localStorage;
    try {
      let supported = false;
      requestAnimationFrame(() => {
        try {
          storage.setItem(testKey, '1');
          storage.removeItem(testKey);
          supported = true;
        } catch {
          supported = false;
        }
      });
      return (function wait() {
        return supported;
      })();
    } catch {
      return false;
    }
  };

  theme.getElementWidth = function (element, value) {
    const style = window.getComputedStyle(element);
    if (!theme._widthMeasurer) {
      const span = document.createElement('span');
      span.style.position = 'absolute';
      span.style.visibility = 'hidden';
      span.style.whiteSpace = 'pre';
      span.style.pointerEvents = 'none';
      span.style.zIndex = '-1';
      document.body.appendChild(span);
      theme._widthMeasurer = span;
    }
    const measurer = theme._widthMeasurer;
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        measurer.style.fontFamily = style.fontFamily;
        measurer.style.fontSize = style.fontSize;
        measurer.style.fontWeight = style.fontWeight;
        measurer.textContent = value;
        resolve(measurer.getBoundingClientRect().width);
      });
    });
  };

  theme.Carousel = (function () {
    class Carousel {
      constructor(container, options, navigation) {
        this.container = container;
        this.options = options;
        this.prevButton = navigation.previous;
        this.nextButton = navigation.next;
        this._boundPrevClick = this.onPrevButtonClick.bind(this);
        this._boundNextClick = this.onNextButtonClick.bind(this);
        this._boundDragStart = this.onDragStartHandler.bind(this);
        this._boundSelect = this.onSelectHandler.bind(this);
      }
      load() {
        this.slider = new Flickity(this.container, this.options);
        this.prevButton.addEventListener('click', this._boundPrevClick);
        this.nextButton.addEventListener('click', this._boundNextClick);
        this.slider.on('dragStart', this._boundDragStart);
        this.slider.on('select', this._boundSelect);
        this.onSelectHandler();
      }
      onDragStartHandler() {
        requestAnimationFrame(() => {
          this.prevButton.style.transform = '';
          const prevFill = this.prevButton.querySelector('btn-text');
          if (prevFill) prevFill.style.transform = '';
          this.nextButton.style.transform = '';
          const nextFill = this.nextButton.querySelector('btn-text');
          if (nextFill) nextFill.style.transform = '';
        });
      }
      onSelectHandler() {
        requestAnimationFrame(() => {
          const idx = this.slider.selectedIndex;
          const slides = this.slider.slides;
          this.prevButton.disabled = idx === 0;
          this.nextButton.disabled = idx === slides.length - 1;
        });
      }
      onPrevButtonClick(e) {
        e.preventDefault();
        this.slider.previous();
        requestAnimationFrame(() => {
          this.nextButton.style.transform = '';
          const nextFill = this.nextButton.querySelector('btn-text');
          if (nextFill) nextFill.style.transform = '';
        });
      }
      onNextButtonClick(e) {
        e.preventDefault();
        this.slider.next();
        requestAnimationFrame(() => {
          this.prevButton.style.transform = '';
          const prevFill = this.prevButton.querySelector('btn-text');
          if (prevFill) prevFill.style.transform = '';
        });
      }
      unload() {
        if (this.slider) {
          this.slider.off('dragStart', this._boundDragStart);
          this.slider.off('select', this._boundSelect);
          this.slider.destroy();
          this.slider = null;
        }
        this.prevButton.removeEventListener('click', this._boundPrevClick);
        this.nextButton.removeEventListener('click', this._boundNextClick);
      }
    }
    return Carousel;
  })();

  theme.initWhenVisible = (() => {
    class ScriptLoader {
      constructor(callback, delay = 5000) {
        this.loadScriptTimer = setTimeout(() => {
          requestAnimationFrame(callback);
        }, delay);
        this.userInteractionEvents = ['mouseover', 'mousemove', 'keydown', 'touchstart', 'touchend', 'touchmove', 'wheel'];
        this.onScriptLoader = this.triggerScriptLoader.bind(this, callback);
        window.addEventListener('DOMContentLoaded', this.onScriptLoader, {passive: true});
        document.body.addEventListener('pointerover', this.onScriptLoader, {passive: true});
        this.userInteractionEvents.forEach((event) => {
          window.addEventListener(event, this.onScriptLoader, {passive: true});
        });
      }
      triggerScriptLoader(callback) {
        requestAnimationFrame(callback);
        clearTimeout(this.loadScriptTimer);
        window.removeEventListener('DOMContentLoaded', this.onScriptLoader, {passive: true});
        document.body.removeEventListener('pointerover', this.onScriptLoader, {passive: true});
        this.userInteractionEvents.forEach((event) => {
          window.removeEventListener(event, this.onScriptLoader, {passive: true});
        });
      }
    }
    return ScriptLoader;
  })();

  theme.config = theme.config || {};
  theme.config.hasSessionStorage = theme.isStorageSupported('session');
  theme.config.hasLocalStorage = theme.isStorageSupported('local');

  const mql = window.matchMedia(theme.config.mediaQuerySmall || '(max-width: 768px)');
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (mql) => {
    if (mql.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent('matchSmall'));
    } else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent('unmatchSmall'));
    }
  };

  new theme.initWhenVisible(() => {
    document.body.removeAttribute('layout-load');
  });
})();

/* -------------------------------------------------------------------------- */
/* SectionRevel */
/* -------------------------------------------------------------------------- */

theme.SectionRevel = function () {
  const debounce = (fn, wait = 80) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  function revealSection(sectionElement) {
    if (!sectionElement || sectionElement.classList.contains('visible')) return;
    sectionElement.classList.remove('placeholder');
    sectionElement.classList.add('visible');

    const sectionId = sectionElement.dataset.sectionId || sectionElement.id;
    if (sectionId) {
      document.dispatchEvent(
        new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: {
            sectionId: sectionId.replace('shopify-section-', ''),
          },
        })
      );
    }

    if (typeof gsap !== 'undefined') {
      requestAnimationFrame(() => {
        gsap.fromTo(
          sectionElement,
          {autoAlpha: 0},
          {
            autoAlpha: 1,
            duration: 0.2,
            ease: '0.25,0.8,0.25,1',
            overwrite: 'auto',
          }
        );
      });
    } else {
      requestAnimationFrame(() => {
        sectionElement.style.opacity = 1;
        sectionElement.style.visibility = 'visible';
      });
    }
  }

  function observeSections(sections) {
    if (!sections.length) return;
    if ('IntersectionObserver' in window) {
      const observerOptions = {
        root: null,
        rootMargin: '0px 0px 120px 0px',
        threshold: 0.01,
      };

      const sectionObserver = new IntersectionObserver((entries, observer) => {
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (entry.isIntersecting) {
            requestAnimationFrame(() => {
              revealSection(entry.target);
            });
            observer.unobserve(entry.target);
          }
        }
      }, observerOptions);

      for (let i = 0; i < sections.length; i++) {
        sectionObserver.observe(sections[i]);
      }
    } else {
      for (let i = 0; i < sections.length; i++) {
        requestAnimationFrame(() => {
          revealSection(sections[i]);
        });
      }
    }
  }

  const handleMutations = debounce((mutations) => {
    for (let i = 0; i < mutations.length; i++) {
      const mutation = mutations[i];
      for (let j = 0; j < mutation.addedNodes.length; j++) {
        const node = mutation.addedNodes[j];
        if (node.nodeType === 1 && node.classList.contains('shopify-section') && node.classList.contains('placeholder')) {
          observeSections([node]);
        }
      }
    }
  }, 80);

  theme.DOMready(() => {
    const lazySections = Array.prototype.slice.call(document.querySelectorAll('.shopify-section.placeholder'));
    observeSections(lazySections);

    if ('MutationObserver' in window) {
      const observer = new MutationObserver(handleMutations);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  });
};
theme.SectionRevel();

/* -------------------------------------------------------------------------- */
/* Sliders */
/* -------------------------------------------------------------------------- */

new theme.initWhenVisible(() => {
  let isTouching = false;
  let startCoords;

  const onTouchStart = (e) => {
    const target = e.target.closest('.flickity-slider');
    if (!target) {
      isTouching = false;
      return;
    }
    isTouching = true;
    startCoords = {
      x: e.touches[0].pageX,
      y: e.touches[0].pageY,
    };
  };

  const onTouchMove = (e) => {
    if (!isTouching || !e.cancelable) return;
    const dx = e.touches[0].pageX - startCoords.x;
    if (Math.abs(dx) > Flickity.defaults.dragThreshold) {
      requestAnimationFrame(() => {
        e.preventDefault();
      });
    }
  };

  document.body.addEventListener('touchstart', onTouchStart, theme.supportsPassive ? {passive: true} : false);

  document.body.addEventListener('touchmove', onTouchMove, theme.supportsPassive ? {passive: false} : false);
});

/* -------------------------------------------------------------------------- */
/* Header */
/* -------------------------------------------------------------------------- */

class StickyHeader extends HTMLElement {
  constructor() {
    super();
    this.lastScrollTop = 0;
    this.onScroll = theme.utils.throttle(this.onScrollHandler.bind(this), 50);
    this.setHeight = theme.utils.debounce(this.setHeightHandler.bind(this), 50);
  }

  connectedCallback() {
    this.headerSection = document.querySelector('.header-section');
    if (!this.headerSection) return;
    this.init();
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.setHeight());
      this.resizeObserver.observe(this);
    }
    this.header = this.headerSection.querySelector('header');
    this.headerBounds = this.header ? this.header.getBoundingClientRect() : {height: 0};
    this.headerSection.classList.add('header-sticky');
    window.addEventListener('scroll', this.onScroll, {passive: true});
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll, {passive: true});
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  init() {
    new theme.initWhenVisible(this.setHeight);
    if (window.location.pathname === '/' || document.body.classList.contains('template-index')) {
      this.headerSection.classList.add('header-transparent', 'no-animate');
      setTimeout(() => {
        this.headerSection.classList.remove('no-animate');
      }, 500);
    } else {
      this.headerSection.classList.remove('header-transparent');
    }
  }

  setHeightHandler() {
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--header-height', Math.round(this.clientHeight) + 'px');
      if (this.classList.contains('header--center') && !document.documentElement.style.getPropertyValue('--header-nav-height')) {
        const menuToggle = document.getElementById('MenuToggle');
        document.documentElement.style.setProperty('--header-nav-height', menuToggle ? Math.round(menuToggle.clientHeight) + 'px' : '0px');
      }
    });
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;
    const scrolledUp = scrollTop < this.lastScrollTop;
    const pastHeader = scrollTop > window.innerHeight * 0.05;
    requestAnimationFrame(() => {
      this.headerSection.classList.toggle('header-scrolled', pastHeader);
      if (pastHeader && scrolledUp) {
        this.headerSection.classList.remove('header-hidden');
      } else if (pastHeader && !scrolledUp) {
        this.headerSection.classList.add('header-hidden');
      } else {
        this.headerSection.classList.remove('header-hidden');
      }
      this.lastScrollTop = scrollTop;
    });
  }
}
customElements.define('sticky-header', StickyHeader);

/* -------------------------------------------------------------------------- */
/* Buttons/Links */
/* -------------------------------------------------------------------------- */

class RevealLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.revealButton = new theme.RevealButton(this);
    this.revealButton.load();
  }
}
customElements.define('reveal-link', RevealLink, {extends: 'a'});

class HoverLink extends HTMLAnchorElement {
  constructor() {
    super();

    // this.hoverButton = new theme.HoverButton(this);
    // this.hoverButton.load();
  }
}
customElements.define('hover-link', HoverLink, {extends: 'a'});

class MagnetLink extends HoverLink {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-link', MagnetLink, {extends: 'a'});

class HoverButton extends HTMLButtonElement {
  constructor() {
    super();

    if (this.type === 'submit' && this.form) {
      this.form.addEventListener('submit', () => this.setAttribute('aria-busy', 'true'));
    }

    window.addEventListener('pageshow', () => this.removeAttribute('aria-busy'));
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return (this._contentElement = this._contentElement || this.querySelector('.btn-text'));
  }

  attributeChangedCallback(_name, _oldValue, newValue) {
    if (newValue === 'true') {
      this.contentElement && (this.contentElement.style.opacity = 0.5);
    } else {
      this.contentElement && (this.contentElement.style.opacity = 1);
    }
  }
}
customElements.define('hover-button', HoverButton, {extends: 'button'});

class MagnetButton extends HoverButton {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-button', MagnetButton, {extends: 'button'});

class HoverElement extends HTMLElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define('hover-element', HoverElement);

class MagnetElement extends HoverElement {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-element', MagnetElement);

/* -------------------------------------------------------------------------- */
/* Animate Element */
/* -------------------------------------------------------------------------- */

class AnimateElement extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        async (entries, observer) => {
          entries.forEach(async (entry) => {
            if (entry.isIntersecting) {
              requestAnimationFrame(async () => {
                if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
                this.animation.load();
              });
              observer.disconnect();
            }
          });
        },
        {rootMargin: '0px 0px 200px 0px'}
      );
      this._observer.observe(this);
    } else {
      requestAnimationFrame(async () => {
        if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
        this.animation.load();
      });
    }
  }

  reset() {
    this.animation.reset();
  }

  refresh() {
    this.animation.reload();
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }
}
customElements.define('animate-element', AnimateElement);

class AnimatePicture extends HTMLPictureElement {
  constructor() {
    super();
    this._onEnter = this._onEnter.bind(this);
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;

    this._lazyLoadImages();

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this._setupAnimation());
    } else {
      setTimeout(() => this._setupAnimation(), 0);
    }
  }

  _lazyLoadImages() {
    this.querySelectorAll('img').forEach((img) => {
      if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
      if (img.dataset.src) img.src = img.dataset.src;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
    });
  }

  _setupAnimation() {
    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              requestAnimationFrame(this._onEnter);
              this._observer.disconnect();
            }
          });
        },
        {rootMargin: '0px 0px 200px 0px'}
      );
      this._observer.observe(this);
    } else {
      requestAnimationFrame(this._onEnter);
    }
  }

  async _onEnter() {
    const imgs = this.querySelectorAll('img');
    if (imgs.length) await theme.utils.imageLoaded(imgs);
    this.animation.load();
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
  }
}
customElements.define('animate-picture', AnimatePicture, {extends: 'picture'});

/* -------------------------------------------------------------------------- */
/* AnnoucementBar */
/* -------------------------------------------------------------------------- */

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.init.bind(this));
    } else {
      requestAnimationFrame(() => {
        if ('IntersectionObserver' in window) {
          this._observer = new IntersectionObserver(
            (entries, observer) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  requestAnimationFrame(() => this.init());
                  observer.disconnect();
                }
              });
            },
            {rootMargin: '0px 0px 200px 0px'}
          );
          this._observer.observe(this);
        } else {
          requestAnimationFrame(() => this.init());
        }
      });
    }
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
  }

  init() {
    requestAnimationFrame(() => {
      if (this.items.length > 1) {
        this.slider = new Flickity(this, {
          accessibility: false,
          fade: true,
          pageDots: false,
          prevNextButtons: false,
          wrapAround: true,
          rightToLeft: theme.config.rtl,
          autoPlay: this.autoplay ? this.speed : false,
          on: {
            ready: function () {
              requestAnimationFrame(() => {
                this.element.setAttribute('loaded', '');
              });
            },
          },
        });

        this.slider.on('change', this.onChange.bind(this));
        this.addEventListener('slider:previous', () => this.slider.previous());
        this.addEventListener('slider:next', () => this.slider.next());
        this.addEventListener('slider:play', () => this.slider.playPlayer());
        this.addEventListener('slider:pause', () => this.slider.pausePlayer());

        if (Shopify.designMode) {
          this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)));
        }
      }
    });
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
    if (this._observer) this._observer.disconnect();
  }

  onChange() {
    requestAnimationFrame(() => {
      this.dispatchEvent(
        new CustomEvent('slider:change', {
          bubbles: true,
          detail: {currentPage: this.slider.selectedIndex},
        })
      );
    });
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* -------------------------------------------------------------------------- */
/* Accordion */
/* -------------------------------------------------------------------------- */

class AccordionsDetails extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('toggle', this.onToggle);
  }

  get items() {
    return (this._items = this._items || Array.from(this.querySelectorAll('details[is="accordion-details"]')));
  }

  onToggle(event) {
    const {current: target, open} = event.detail;
    this.items.forEach((item) => {
      if (item !== target) {
        item.close();
      }
    });

    if (open) {
      let headerHeight = 0;
      if (!theme.config.mqlSmall && document.querySelector('header.header')) {
        headerHeight = Math.round(document.querySelector('header.header').clientHeight);
      }
      setTimeout(() => {
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - headerHeight,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth',
        });
      }, 250);
    }
  }
}
customElements.define('accordions-details', AccordionsDetails);

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this._open = this.hasAttribute('open');
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('summary + *');
    this.setAttribute('aria-expanded', this._open ? 'true' : 'false');

    this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        if (this.designModeActive) this.open = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        if (this.designModeActive) this.open = false;
      });
    }
  }

  get designModeActive() {
    return true;
  }

  get controlledElement() {
    return this.closest('accordions-details');
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get open() {
    return this._open;
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }

    this.setAttribute('aria-expanded', value ? 'true' : 'false');
    this.dispatchEventHandler();
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.open = !this.open;
  }

  close() {
    this._open = false;
    this.transition(false);
  }

  async transition(value) {
    this.style.overflow = 'hidden';

    if (value) {
      this.setAttribute('open', '');

      const startHeight = this.summaryElement.clientHeight;
      const endHeight = this.scrollHeight;
      this.style.height = `${startHeight}px`;

      const tlOpen = gsap.timeline();
      tlOpen
        .to(this, {
          height: endHeight,
          duration: 0.335,
          ease: '0.25,0.8,0.25,1',
        })
        .fromTo(this.contentElement, {opacity: 0, y: 10}, {opacity: 1, y: 0, duration: 0.335, ease: '0.25,0.8,0.25,1'}, '-=0.1');
      await tlOpen;
    } else {
      const tlClose = gsap.timeline();
      const endHeight = this.summaryElement.clientHeight;

      tlClose
        .to(this.contentElement, {
          opacity: 0,
          duration: 0.335,
          ease: '0.25,0.8,0.25,1',
        })
        .to(
          this,
          {
            height: endHeight,
            duration: 0.335,
            ease: '0.25,0.8,0.25,1',
          },
          '<'
        );
      await tlClose;

      this.removeAttribute('open');
    }

    this.style.height = 'auto';
    this.style.overflow = 'visible';
  }

  dispatchEventHandler() {
    (this.controlledElement ?? this).dispatchEvent(
      new CustomEvent('toggle', {
        bubbles: true,
        detail: {current: this, open: this.open},
      })
    );
  }
}
customElements.define('accordion-details', AccordionDetails, {
  extends: 'details',
});

/* -------------------------------------------------------------------------- */
/* Modal/Drawer */
/* -------------------------------------------------------------------------- */

class OverlayElement extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('mousedown', this.onMouseDown);
    this.addEventListener('mouseup', this.onMouseUp);
    this.addEventListener('mouseleave', this.onMouseLeave);
  }

  onMouseDown() {
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.stop === 'function') {
      theme.lenis.lenis.stop();
    }
  }
  onMouseUp() {
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.start === 'function') {
      theme.lenis.lenis.start();
    }
  }
  onMouseLeave() {
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.start === 'function') {
      theme.lenis.lenis.start();
    }
  }
}
customElements.define('overlay-element', OverlayElement);

const lockLayerCount = new WeakMap();
class ModalElement extends HTMLElement {
  constructor() {
    super();

    this.events = {
      afterHide: 'modal:afterHide',
      afterShow: 'modal:afterShow',
    };

    this.classes = {
      open: 'has-modal-open',
      opening: 'has-modal-opening',
    };
  }

  static get observedAttributes() {
    return ['id', 'open'];
  }

  get shouldLock() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get overlay() {
    return (this._overlay = this._overlay || this.querySelector('.fixed-modal'));
  }

  get gesture() {
    return (this._gesture = this._gesture || this.querySelector('gesture-element'));
  }

  get designMode() {
    return this.hasAttribute('shopify-design-mode');
  }

  get focusElement() {
    return this.querySelector('button');
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.controls.forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this), {
        signal: this.abortController.signal,
      })
    );
    document.addEventListener('keyup', (event) => event.code === 'Escape' && this.hide(), {
      signal: this.abortController.signal,
    });

    if (this.gesture) {
      this.gestureConfig = {
        animationFrame: null,
        moveY: 0,
        maxGestureDistance: 0,
        endPoint: 0,
        layerHeight: null,
      };

      this.gestureWrap = this.gesture.parentElement;

      setTimeout(() => {
        this.gesture.on('panstart', this.onPanStart.bind(this));
        this.gesture.on('panmove', this.onPanMove.bind(this));
        this.gesture.on('panend', this.onPanEnd.bind(this));
      }, 75);
    }

    if (Shopify.designMode && this.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:select', (event) => this.show(null, !event.detail.load), {
        signal: this.abortController.signal,
      });
      section.addEventListener('shopify:section:deselect', this.hide.bind(this), {
        signal: this.abortController.signal,
      });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));

        if (oldValue === null && (newValue === '' || newValue === 'immediate')) {
          this.hidden = false;
          this.removeAttribute('inert');

          this.originalParentBeforeAppend = null;
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this.originalParentBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const showTransitionPromise = this.showTransition(newValue !== 'immediate') || Promise.resolve();
          showTransitionPromise.then(() => {
            this.afterShow();
            this.dispatchEvent(new CustomEvent(this.events.afterShow, {bubbles: true}));
          });
        } else if (oldValue !== null && newValue === null) {
          this.setAttribute('inert', '');

          const hideTransitionPromise = this.hideTransition() || Promise.resolve();
          hideTransitionPromise.then(() => {
            this.afterHide();

            if (!this.hasAttribute('inert')) return;

            if (this.parentElement === document.body && this.originalParentBeforeAppend) {
              this.originalParentBeforeAppend.appendChild(this);
              this.originalParentBeforeAppend = null;
            }
            this.dispatchEvent(new CustomEvent(this.events.afterHide, {bubbles: true}));

            this.hidden = true;
          });
        }
        this.dispatchEvent(new CustomEvent('toggle', {bubbles: true}));

        break;
    }
  }

  onButtonClick(event) {
    event.preventDefault();

    this.open ? this.hide() : this.show(event.currentTarget);
  }

  hide() {
    if (!this.open) return;

    this.beforeHide();
    this.resetGesture();
    this.removeAttribute('open');

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }
  show(activeElement = null, animate = true) {
    if (this.open) return;

    this.beforeShow();
    this.activeElement = activeElement;
    this.setAttribute('open', animate ? '' : 'immediate');

    if (this.shouldLock) {
      document.body.classList.add(this.classes.opening);
    }

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  beforeHide() {
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.start === 'function') {
      theme.lenis.lenis.start();
    }
  }
  beforeShow() {
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.stop === 'function') {
      theme.lenis.lenis.stop();
    }
  }

  afterHide() {
    setTimeout(() => {
      theme.a11y.removeTrapFocus(this.activeElement);
      if (this.shouldLock) {
        lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) - 1);

        document.body.classList.toggle(this.classes.open, lockLayerCount.get(ModalElement) > 0);
      }
    });
  }
  afterShow() {
    theme.a11y.trapFocus(this, this.focusElement);
    if (this.shouldLock) {
      lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) + 1);

      document.body.classList.remove(this.classes.opening);
      document.body.classList.add(this.classes.open);
    }
  }

  showTransition() {
    setTimeout(() => {
      this.setAttribute('active', '');
    }, 75);
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, {once: true});
    });
  }
  hideTransition() {
    this.removeAttribute('active');
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, {once: true});
    });
  }

  resetGesture() {
    if (this.gesture) {
      this.gestureWrap.style.transform = '';
      this.gestureWrap.style.transition = '';
      this.overlay.style.opacity = '';
      this.overlay.style.transition = '';
    }
  }

  onPanStart() {
    this.removeTransition(this.gestureWrap, 'transform');

    if (this.hasAttribute('open')) {
      if (this.gestureConfig.layerHeight === null) {
        this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
      }
      this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;
      this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;
    }
  }

  onPanMove() {
    if (this.gestureConfig.animationFrame) return;

    this.gestureConfig.animationFrame = requestAnimationFrame(() => {
      const clamp = (a, min = 0, max = 1) => Math.min(max, Math.max(min, a));
      const invlerp = (x, y, a) => clamp((a - x) / (y - x));

      // Give an indication of whether we've passed the swiping threshold.
      this.gestureWrap.style.transition = 'transform .1s linear';
      this.overlay.style.transition = 'opacity .1s linear';

      if (this.hasAttribute('open')) {
        if (this.gestureConfig.layerHeight === null) {
          this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
        }

        this.gestureConfig.moveY = this.gesture.touchMoveY;
        this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;

        if (this.gestureConfig.moveY >= 0) {
          this.gestureWrap.style.transform = `translateY(${Math.min(this.gestureConfig.moveY, this.gestureConfig.maxGestureDistance)}px)`;

          this.overlay.style.opacity = invlerp(this.gestureConfig.layerHeight, 0, this.gestureConfig.moveY);
        } else {
          this.gestureWrap.style.transform = 'translateY(0)';
          this.gestureWrap.style.transition = '';
          this.overlay.style.opacity = '1';
        }
      }

      this.gestureConfig.animationFrame = null;
    });
  }

  onPanEnd() {
    this.gestureConfig.animationFrame === null || cancelAnimationFrame(this.gestureConfig.animationFrame);
    this.gestureConfig.animationFrame = null;
    this.gestureWrap.style.transition = 'transform .1s linear';

    if (this.gestureConfig.layerHeight === null) {
      this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
    }

    this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;

    if (this.hasAttribute('open')) {
      this.gestureConfig.moveY = this.gesture.touchMoveY;

      if (this.gestureConfig.moveY < this.gestureConfig.endPoint) {
        this.gestureWrap.style.transform = 'translateY(0)';
      } else {
        this.gestureWrap.style.transform = `translateY(${this.gestureConfig.layerHeight}px)`;

        this.gestureWrap.addEventListener(
          this.whichTransitionEvent(),
          () => {
            this.hide();
          },
          {once: true}
        );
      }
    }
  }

  removeTransition(node, transition) {
    const match = node.style.transition.match(new RegExp('(?:^|,)\\s*' + transition + '(?:$|\\s|,)[^,]*', 'i'));
    if (match) {
      const transitionArray = node.style.transition.split('');
      transitionArray.splice(match.index, match[0].length);
      node.style.transition = transitionArray.join('');
    }
  }

  whichTransitionEvent() {
    let t;
    const el = document.createElement('fakeelement');
    const transitions = {
      WebkitTransition: 'webkitTransitionEnd',
      MozTransition: 'transitionend',
      MSTransition: 'msTransitionEnd',
      OTransition: 'oTransitionEnd',
      transition: 'transitionEnd',
    };

    for (t in transitions) {
      if (el.style[t] !== undefined) {
        return transitions[t];
      }
    }
  }
}
customElements.define('modal-element', ModalElement);
lockLayerCount.set(ModalElement, 0);

class DrawerElement extends ModalElement {
  constructor() {
    super();
    this.events = {
      afterHide: 'drawer:afterHide',
      afterShow: 'drawer:afterShow',
    };
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }

  afterShow() {
    super.afterShow();
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.stop === 'function') {
      theme.lenis.lenis.stop();
    }
  }

  afterHide() {
    super.afterHide();
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.start === 'function') {
      theme.lenis.lenis.start();
    }
  }
}
customElements.define('drawer-element', DrawerElement);

class MenuDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return (this._menuItems = this._menuItems || this.querySelectorAll('.drawer__menu:not(.active)>li'));
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      gsap.fromTo(
        this.menuItems,
        {x: -20, opacity: 0},
        {
          x: 0,
          opacity: 1,
          duration: 0.735,
          ease: '0.25,0.8,0.25,1',
          stagger: 0.1,
          onComplete: () => {
            this.menuItems.forEach((item) => item.removeAttribute('style'));
          },
        }
      );
    }, 300);
  }

  beforeHide() {
    super.beforeHide();

    setTimeout(() => {
      this.querySelectorAll('details[is=menu-details]').forEach((subMenu) => {
        subMenu.onCloseButtonClick();
      });
    }, 300);
  }
}
customElements.define('menu-drawer', MenuDrawer);

class ShareDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return (this._menuItems = this._menuItems || this.querySelectorAll('.share-buttons>li'));
  }

  get urlToShare() {
    const urlInput = this.querySelector('input[type=hidden]');
    return urlInput ? urlInput.value : document.location.href;
  }

  connectedCallback() {
    if (navigator.share) {
      this.controls.forEach((button) => button.addEventListener('click', this.shareTo.bind(this)));
    } else {
      super.connectedCallback();
    }
  }

  async shareTo(event) {
    event.preventDefault();
    try {
      await navigator.share({url: this.urlToShare, title: document.title});
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Share canceled by user');
      } else {
        console.error(error);
      }
    }
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      gsap.fromTo(
        this.menuItems,
        {y: '2.5rem', opacity: 0},
        {
          y: 0,
          opacity: 1,
          duration: 0.735,
          ease: '0.25,0.8,0.25,1', // Equivalent to [0.76, 0, 0.24, 1]
          stagger: 0.1,
          onComplete: () => {
            this.menuItems.forEach((item) => item.removeAttribute('style'));
          },
        }
      );
    }, 300);
  }
}
customElements.define('share-drawer', ShareDrawer);

class BackInStockDrawer extends DrawerElement {
  constructor() {
    super();

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.init.bind(this));
    } else {
      this.init();
    }
  }

  get submited() {
    return this.querySelector('.alert') !== null;
  }

  init() {
    if (this.submited) {
      setTimeout(() => {
        this.show();
      }, 1000);
    }
  }
}
customElements.define('back-in-stock-drawer', BackInStockDrawer);

class MenuDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  onSummaryClick() {
    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
    }, 100);
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        requestAnimationFrame(handleAnimation);
      } else {
        this.removeAttribute('open');
      }
    };

    requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-details', MenuDetails, {extends: 'details'});

/* -------------------------------------------------------------------------- */
/* Quantity */
/* -------------------------------------------------------------------------- */

class QuantityLabel extends HTMLLabelElement {
  constructor() {
    super();
    this.scalingAnimation = null;
  }

  connectedCallback() {
    this.querySelector('.quantity__rules-cart').append(this.animationElement);
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return (this._contentElement = this._contentElement || this.querySelector('.quantity-cart'));
  }

  get animationElement() {
    return (this._animationElement =
      this._animationElement ||
      document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild);
  }

  attributeChangedCallback(_name, _oldValue, newValue) {
    if (newValue === 'true') {
      if (this.scalingAnimation) {
        this.scalingAnimation.kill();
      }

      const tl = gsap.timeline();
      tl.to(this.contentElement, {opacity: 0, duration: 0.15}).to(this.animationElement, {opacity: 1, duration: 0.15});

      this.scalingAnimation = gsap.fromTo(
        this.animationElement.children,
        {scale: 1.6},
        {
          scale: 0.6,
          duration: 0.35,
          stagger: 0.175, // 0.35 / 2
          yoyo: true,
          repeat: -1, // for Infinity
          ease: '0.25,0.8,0.25,1',
        }
      );
    } else {
      if (this.scalingAnimation) {
        this.scalingAnimation.kill();
        this.scalingAnimation = null;
      }
      gsap.set(this.animationElement.children, {clearProps: 'scale'});

      const tl = gsap.timeline();
      tl.to(this.animationElement, {opacity: 0, duration: 0.15}).to(this.contentElement, {opacity: 1, duration: 0.15});
    }
  }
}
customElements.define('quantity-label', QuantityLabel, {extends: 'label'});

class QuantityInput extends HTMLElement {
  quantityUpdateUnsubscriber = undefined;
  quantityBoundriesUnsubscriber = undefined;
  quantityRulesUnsubscriber = undefined;

  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get input() {
    return this.querySelector('input');
  }

  get value() {
    return this.input.value;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.buttons = Array.from(this.querySelectorAll('button'));
    this.changeEvent = new Event('change', {bubbles: true});
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.input.addEventListener('focus', () => setTimeout(() => this.input.select()));

    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)), {
      signal: this.abortController.signal,
    });

    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    this.quantityBoundriesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, this.setQuantityBoundries.bind(this));
    this.quantityRulesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityRules, this.updateQuantityRules.bind(this));
  }

  disconnectedCallback() {
    this.abortController.abort();

    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    if (this.quantityBoundriesUnsubscriber) {
      this.quantityBoundriesUnsubscriber();
    }
    if (this.quantityRulesUnsubscriber) {
      this.quantityRulesUnsubscriber();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.getAttribute('data-min')) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.getAttribute('data-min');
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.getAttribute('data-min') === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  onInputChange() {
    if (this.input.value === '') {
      this.input.value = parseInt(this.input.min);
    }
    this.validateQtyRules();
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]');
      if (buttonMinus) buttonMinus.toggleAttribute('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector('button[name="plus"]');
      if (buttonPlus) buttonPlus.toggleAttribute('disabled', parseInt(value) >= parseInt(this.input.max));
    }
  }

  updateQuantityRules({data: {sectionId, productId, parsedHTML}}) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
    const quantityFormUpdated = parsedHTML.getElementById(`QuantityForm-${sectionId}-${this.productId}`);
    const quantityForm = this.closest(`#QuantityForm-${sectionId}-${this.productId}`);
    for (let selector of selectors) {
      const current = quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;

      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          } else {
            current.removeAttribute(attribute);
          }
        }
      } else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  setQuantityBoundries({data: {sectionId, productId}}) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const data = {
      cartQuantity: this.input.hasAttribute('data-cart-quantity') ? parseInt(this.input.getAttribute('data-cart-quantity')) : 0,
      min: this.input.hasAttribute('data-min') ? parseInt(this.input.getAttribute('data-min')) : 1,
      max: this.input.hasAttribute('data-max') ? parseInt(this.input.getAttribute('data-max')) : null,
      step: this.input.hasAttribute('step') ? parseInt(this.input.getAttribute('step')) : 1,
    };

    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    this.input.min = min;

    if (max) {
      this.input.max = max;
    } else {
      this.input.removeAttribute('max');
    }
    this.input.value = min;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined);
  }

  reset() {
    this.input.value = this.input.defaultValue;
  }
}
customElements.define('quantity-input', QuantityInput);

class CartCount extends HTMLElement {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText);
  }

  onCartUpdate(event) {
    if (event.cart.errors) return;

    this.innerText = event.cart.item_count;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('cart-count', CartCount);

/* -------------------------------------------------------------------------- */
/* Products */
/* -------------------------------------------------------------------------- */

class RecentlyViewed extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.hasLocalStorage) {
      this.hidden = true;
      return;
    }

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              requestAnimationFrame(() => this.init());
              observer.disconnect();
            }
          });
        },
        {rootMargin: '0px 0px 600px 0px'}
      );
      this._observer.observe(this);
    } else {
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    fetch(this.getAttribute('data-url') + this.getQueryString())
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser().parseFromString(responseText, 'text/html').querySelector('.shopify-section');

        const recommendations = sectionInnerHTML.querySelector('recently-viewed');
        if (recommendations && recommendations.innerHTML.trim().length) {
          requestAnimationFrame(() => {
            this.innerHTML = recommendations.innerHTML;
            this.dispatchEvent(new CustomEvent('recentlyViewed:loaded'));
          });
        } else {
          requestAnimationFrame(() => {
            this.closest('.recently-section')?.remove();
            this.dispatchEvent(new CustomEvent('is-empty'));
          });
        }
      });
  }

  getQueryString() {
    let recentlyViewed = '[]';
    if (theme.config.hasLocalStorage) {
      recentlyViewed = window.localStorage.getItem(`${theme.settings.themeName}:recently-viewed`) || '[]';
    }

    const items = new Set(JSON.parse(recentlyViewed));
    if (this.hasAttribute('data-product-id')) {
      items.delete(parseInt(this.getAttribute('data-product-id')));
    }
    return Array.from(items.values(), (item) => `id:${item}`)
      .slice(0, this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 4)
      .join(' OR ');
  }
}
customElements.define('recently-viewed', RecentlyViewed);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              requestAnimationFrame(() => this.init());
              observer.disconnect();
            }
          });
        },
        {rootMargin: '0px 0px 600px 0px'}
      );
      this._observer.observe(this);
    } else {
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    fetch(this.getAttribute('data-url'))
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser().parseFromString(responseText, 'text/html').querySelector('.shopify-section');

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) {
          requestAnimationFrame(() => {
            this.innerHTML = recommendations.innerHTML;
            this.dispatchEvent(new CustomEvent('recommendations:loaded'));
          });
        } else {
          requestAnimationFrame(() => {
            this.closest('.shopify-section')?.remove();
            this.dispatchEvent(new CustomEvent('is-empty'));
          });
        }
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class ProductComplementary extends HTMLElement {
  constructor() {
    super();
  }

  get container() {
    return this.closest('product-recommendations');
  }

  get prevButton() {
    return this.container.querySelector('[data-prev-button]');
  }

  get nextButton() {
    return this.container.querySelector('[data-next-button]');
  }

  connectedCallback() {
    if (this.innerHTML.trim().length) {
      if (this.classList.contains('flickity')) {
        this.carousel = new theme.Carousel(
          this,
          {
            prevNextButtons: false,
            adaptiveHeight: true,
            pageDots: false,
            contain: true,
            cellAlign: 'center',
            rightToLeft: theme.config.rtl,
          },
          {
            previous: this.prevButton,
            next: this.nextButton,
          }
        );

        this.carousel.load();
      }
    } else {
      this.container.hidden = true;
    }
  }

  disconnectedCallback() {
    if (this.carousel) {
      this.carousel.unload();
    }
  }
}
customElements.define('product-complementary', ProductComplementary);

/* -------------------------------------------------------------------------- */
/* Media */
/* -------------------------------------------------------------------------- */

class MediaElement extends HTMLElement {
  constructor() {
    super();
  }

  get media() {
    return Array.from(this.querySelectorAll('picture>img, video, iframe, svg, video-media, g-map')).filter((el) => el.offsetParent !== null);
  }

  connectedCallback() {
    if (this.media.length === 0) {
      this.hidden = true;
      return;
    }

    this.media.forEach((media) => {
      if (media.tagName === 'VIDEO') {
        media.setAttribute('playsinline', '');
        media.setAttribute('muted', '');
        media.setAttribute('autoplay', '');
        media.setAttribute('loop', '');
      }
      if (media.tagName === 'PICTURE' && media.querySelector('img')) {
        media.querySelector('img').setAttribute('loading', 'lazy');
      }
    });

    this.dispatchEvent(new CustomEvent('media:loaded'));
  }
}
customElements.define('media-element', MediaElement);

/* -------------------------------------------------------------------------- */
/* Revel Text */
/* -------------------------------------------------------------------------- */

// class AppearTitle extends HTMLElement {
//   constructor() {
//     super();
//   }

//   connectedCallback() {
//     if (!document.body.hasAttribute('data-title-animation')) return;

//     gsap.registerPlugin(SplitText);

//     let split, animation;

//     const setup = () => {
//       split && split.revert();
//       animation && animation.revert();
//       split = SplitText.create(this, {
//         type: "chars,words,lines",
//         mask: "lines",
//       });

//       animation = gsap.from(split.lines, {
//         y: "120%",
//         duration: 1.2,
//         ease: "power3.out",
//         stagger: 0.1,
//         yoyo: true,
//       });
//     };

//     setup();
//     window.addEventListener("resize", setup);
//   }
// }
// customElements.define('appear-title', AppearTitle);

/* -------------------------------------------------------------------------- */
/* Dropdowns/Mega Menu */
/* -------------------------------------------------------------------------- */

class DropdownElement extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown',
    };

    this.events = {
      afterHide: 'dropdown:afterHide',
      afterShow: 'dropdown:afterShow',
    };

    this.detectHoverListener = this.detectHover.bind(this);
    this.controls.forEach((button) => {
      button.addEventListener('click', this.onToggleClicked.bind(this));
      button.addEventListener('mouseenter', this.detectHoverListener.bind(this));
      button.addEventListener('mouseleave', this.detectHoverListener.bind(this));
    });

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);
  }

  static get observedAttributes() {
    return ['id', 'open'];
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get container() {
    return this.querySelector('*:first-child');
  }

  attributeChangedCallback(name, _oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));
        break;
    }
  }

  show() {
    document.body.classList.add(this.classes.bodyClass);
    this.setAttribute('open', '');

    document.addEventListener('click', this.detectClickOutsideListener);
    document.addEventListener('keydown', this.detectEscKeyboardListener);
    document.addEventListener('focusout', this.detectFocusOutListener);

    this.afterShow();

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  hide() {
    document.body.classList.remove(this.classes.bodyClass);
    this.removeAttribute('open');

    document.removeEventListener('click', this.detectClickOutsideListener);
    document.removeEventListener('keydown', this.detectEscKeyboardListener);
    document.removeEventListener('focusout', this.detectFocusOutListener);

    this.afterHide();

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }

  onToggleClicked(event) {
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }

  afterShow() {
    Motion.animate(
      this,
      {opacity: [0, 1], visibility: 'visible'},
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.62, 0.05, 0.01, 0.99],
      }
    );
    Motion.animate(
      this.container,
      {transform: ['translateY(-105%)', 'translateY(0)']},
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.62, 0.05, 0.01, 0.99],
      }
    );
  }

  afterHide() {
    Motion.animate(
      this,
      {opacity: 0, visibility: 'hidden'},
      {
        duration: theme.config.motionReduced ? 0 : 0.3,
        easing: [0.62, 0.05, 0.01, 0.99],
      }
    );
    Motion.animate(
      this.container,
      {transform: 'translateY(-105%)'},
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.62, 0.05, 0.01, 0.99],
      }
    );
  }

  detectClickOutside(event) {
    if (!this.parentElement.contains(event.target)) {
      this.hide();
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      this.hide();
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.hide();
    }
  }

  detectHover(event) {
    if (theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.show();
    } else {
      this.hide();
    }
  }
}
customElements.define('dropdown-element', DropdownElement);

class DropdownLocalization extends DropdownElement {
  constructor() {
    super();
  }

  afterShow() {
    Motion.animate(
      this,
      {opacity: [0, 1], visibility: 'visible'},
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.62, 0.05, 0.01, 0.99],
      }
    );
  }

  afterHide() {
    Motion.animate(
      this,
      {opacity: 0, visibility: 'hidden'},
      {
        duration: theme.config.motionReduced ? 0 : 0.3,
        easing: [0.62, 0.05, 0.01, 0.99],
      }
    );
  }
}
customElements.define('dropdown-localization', DropdownLocalization);

const lockDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown-menu',
    };

    this.events = {
      afterHide: 'menu:afterHide',
      afterShow: 'menu:afterShow',
    };

    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute('open');
    this.summaryElement.addEventListener('click', this.onSummaryClicked.bind(this));

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);

    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    this.addEventListener('mouseleave', this.detectHoverListener.bind(this));
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    return this.hasAttribute('trigger') ? this.getAttribute('trigger') : 'click';
  }

  get level() {
    return this.hasAttribute('level') ? this.getAttribute('level') : 'top';
  }

  onSummaryClicked(event) {
    event.preventDefault();

    if (!theme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link') && this.summaryElement.getAttribute('data-link').length > 0) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    } else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) + 1);
      document.body.classList.add(this.classes.bodyClass);

      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      setTimeout(() => this.contentElement.setAttribute('open', ''), 100);
      document.addEventListener('click', this.detectClickOutsideListener);
      document.addEventListener('keydown', this.detectEscKeyboardListener);
      document.addEventListener('focusout', this.detectFocusOutListener);
      await this.transitionIn();
      this.shouldReverse();

      return theme.utils.waitForEvent(this, this.events.afterShow);
    } else {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) - 1);
      document.body.classList.toggle(this.classes.bodyClass, lockDropdownCount.get(DetailsDropdown) > 0);

      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.detectClickOutsideListener);
      document.removeEventListener('keydown', this.detectEscKeyboardListener);
      document.removeEventListener('focusout', this.detectFocusOutListener);
      await this.transitionOut();
      if (!this.open) this.removeAttribute('open');

      return theme.utils.waitForEvent(this, this.events.afterHide);
    }
  }

  async transitionIn() {
    Motion.animate(
      this.contentElement,
      {opacity: [0, 1], visibility: 'visible'},
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.62, 0.05, 0.01, 0.99],
        delay: theme.config.motionReduced ? 0 : 0.2,
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return Motion.animate(
      this.contentElement.firstElementChild,
      {transform: [`translateY(${translateY})`, 'translateY(0)']},
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.62, 0.05, 0.01, 0.99],
      }
    ).finished;
  }

  async transitionOut() {
    Motion.animate(
      this.contentElement,
      {opacity: 0, visibility: 'hidden'},
      {
        duration: theme.config.motionReduced ? 0 : 0.3,
        easing: [0.62, 0.05, 0.01, 0.99],
      }
    );
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return Motion.animate(
      this.contentElement.firstElementChild,
      {transform: `translateY(${translateY})`},
      {
        duration: theme.config.motionReduced ? 0 : 0.6,
        easing: [0.62, 0.05, 0.01, 0.99],
      }
    ).finished;
  }

  detectClickOutside(event) {
    if (!this.contains(event.target) && !(event.target.closest('details') instanceof DetailsDropdown)) {
      this.open = false;
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger !== 'hover' || theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.open = true;
    } else {
      this.open = false;
    }
  }

  shouldReverse() {
    const maxWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (maxWidth > window.innerWidth) {
      this.contentElement.classList.add('should-reverse');
    }
  }
}
customElements.define('details-dropdown', DetailsDropdown, {
  extends: 'details',
});
lockDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => (this.open = true));
      this.addEventListener('shopify:block:deselect', () => (this.open = false));
    }
  }

  async transitionIn() {
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.stop === 'function') {
      theme.lenis.lenis.stop();
    }

    document.body.classList.add('with-mega');
    await gsap.to(this.contentElement.firstElementChild, {
      visibility: 'visible',
      opacity: 1,
      duration: theme.config.motionReduced ? 0 : 0.735,
      ease: '0.25,0.8,0.25,1',
      onStart: () => {
        gsap.set(this.contentElement.firstElementChild, {opacity: 0});
      },
    });
  }

  async transitionOut() {
    document.body.classList.remove('with-mega');
    await gsap.to(this.contentElement.firstElementChild, {
      visibility: 'hidden',
      opacity: 0,
      duration: theme.config.motionReduced ? 0 : 0.735,
      ease: '0.25,0.8,0.25,1',
    });

    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.start === 'function') {
      theme.lenis.lenis.start();
    }
  }

  afterShow() {
    super.afterShow();
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.stop === 'function') {
      theme.lenis.lenis.stop();
    }
  }

  afterHide() {
    super.afterHide();
    if (theme.lenis && theme.lenis.lenis && typeof theme.lenis.lenis.start === 'function') {
      theme.lenis.lenis.start();
    }
  }
}
customElements.define('details-mega', DetailsMega, {extends: 'details'});

/* -------------------------------------------------------------------------- */
/* Forms */
/* -------------------------------------------------------------------------- */

class LocalizationListbox extends HTMLFormElement {
  constructor() {
    super();

    this.items.forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  get items() {
    return (this._items = this._items || Array.from(this.querySelectorAll('a')));
  }

  get input() {
    return this.querySelector('input[name="locale_code"], input[name="country_code"]');
  }

  onItemClick(event) {
    event.preventDefault();

    this.input.value = event.currentTarget.getAttribute('data-value');
    this.submit();
  }
}
customElements.define('localization-listbox', LocalizationListbox, {
  extends: 'form',
});

class LocalizationForm extends HTMLFormElement {
  constructor() {
    super();

    if (theme.config.isTouch) {
      new theme.initWhenVisible(this.init.bind(this));
    } else {
      Motion.inView(this, this.init.bind(this));
    }
  }

  get select() {
    return this.querySelector('select');
  }

  beforeInit() {
    const value = this.select.options[this.select.selectedIndex].text;
    const width = theme.getElementWidth(this.select, value);
    this.select.style.setProperty('--width', `${width}px`);
  }

  init() {
    this.beforeInit();
    this.addEventListener('change', this.submit);
  }
}
customElements.define('localization-form', LocalizationForm, {
  extends: 'form',
});

/* -------------------------------------------------------------------------- */
/* Sticky Element */
/* -------------------------------------------------------------------------- */

class StickyElement extends HTMLElement {
  constructor() {
    super();

    this.endScroll = window.innerHeight - this.offsetHeight - 500;
    this.currPos = window.scrollY;
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;
    this.bottomGap = this.offsetHeight < window.innerHeight ? 20 : 0;

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), {
      capture: true,
      passive: true,
    });
  }

  get headerHeight() {
    const header = document.querySelector('.header-section');
    return header ? Math.round(header.clientHeight) + 20 : this.offsetHeight < window.innerHeight ? 20 : 0;
  }

  get inset() {
    return this.style.getPropertyValue('--inset') || 0;
  }

  onScrollHandler() {
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;

    this.positionStickySidebar();
  }

  positionStickySidebar() {
    this.endScroll = window.innerHeight - this.offsetHeight - this.bottomGap;
    const style = window.getComputedStyle(this);
    const stickyElementTop = parseInt(style.insetBlockStart.replace('px', ''));

    if (this.stickyElementHeight + this.headerHeight + this.bottomGap > this.screenHeight) {
      if (window.scrollY < this.currPos) {
        if (stickyElementTop < this.headerHeight) {
          this.style.insetBlockStart = `${stickyElementTop + this.currPos - window.scrollY}px`;
          this.style.setProperty('--inset', `${stickyElementTop + this.currPos - window.scrollY}px`);
        } else if (stickyElementTop >= this.headerHeight && stickyElementTop !== this.headerHeight) {
          this.style.insetBlockStart = `${this.headerHeight}px`;
          this.style.setProperty('--inset', `${this.headerHeight}px`);
        }
      } else {
        if (stickyElementTop > this.endScroll) {
          this.style.insetBlockStart = `${stickyElementTop + this.currPos - window.scrollY}px`;
          this.style.setProperty('--inset', `${stickyElementTop + this.currPos - window.scrollY}px`);
        } else if (stickyElementTop < this.endScroll && stickyElementTop !== this.endScroll) {
          this.style.insetBlockStart = `${this.endScroll}px`;
          this.style.setProperty('--inset', `${this.endScroll}px`);
        }
      }
    } else {
      this.style.insetBlockStart = `${this.headerHeight}px`;
      this.style.setProperty('--inset', `${this.headerHeight}px`);
    }

    this.currPos = window.scrollY;
  }
}
customElements.define('sticky-element', StickyElement);

/* -------------------------------------------------------------------------- */
/* Carousel Element */
/* -------------------------------------------------------------------------- */

class CarouselElement extends HTMLElement {
  constructor() {
    super();

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              requestAnimationFrame(() => this.init());
              observer.disconnect();
            }
          });
        },
        {rootMargin: '0px 0px 200px 0px'}
      );
      this._observer.observe(this);
    } else {
      requestAnimationFrame(() => this.init());
    }
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }

  get watchCSS() {
    return this.hasAttribute('watch-css');
  }

  get initialIndex() {
    return parseInt(this.getAttribute('initial-index') || 0);
  }

  init() {
    if (this.items.length > 1) {
      this.carousel = new Flickity(this, {
        watchCSS: this.watchCSS,
        prevNextButtons: false,
        adaptiveHeight: true,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        initialIndex: this.initialIndex,
      });

      this.addEventListener('control:select', (event) => this.select(event.detail.index));
      this.carousel.on('change', this.onChange.bind(this));

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.carousel.select(this.items.indexOf(event.target)));
      }
    }
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
    if (this._observer) this._observer.disconnect();
  }

  select(index = 0, immediate = false) {
    if (!immediate) {
      const {selectedIndex, slides} = this.carousel;

      immediate = Math.abs(index - selectedIndex) > 3;

      if (index === 0 && selectedIndex === slides.length - 1) {
        immediate = false;
      }

      if (index === slides.length - 1 && selectedIndex === 0) {
        immediate = false;
      }
    }
    this.carousel.select(index, false, immediate);
  }

  onChange(index) {
    requestAnimationFrame(() => {
      this.dispatchEvent(new CustomEvent('carousel:change', {bubbles: true, detail: {index}}));
    });
  }
}
customElements.define('carousel-element', CarouselElement);

/* -------------------------------------------------------------------------- */
/* Motion List */
/* -------------------------------------------------------------------------- */

// class MotionList extends HTMLElement {
//   constructor() {
//     super();

//     if (theme.config.motionReduced || this.hasAttribute('motion-reduced')) return;

//     this.unload();
//     ScrollTrigger.create({
//       trigger: this,
//       start: '0% 70%',
//       end: '100% 0%',
//       once: true,
//       onEnter: this.load.bind(this),
//     });
//   }

//   get items() {
//     return this.querySelectorAll('.card');
//   }

//   get itemsToShow() {
//     return Array.from(this.items).filter((item) => item.style.display !== 'none');
//   }

//   unload() {
//     gsap.set(this.items, {y: '0em', autoAlpha: 0});
//   }

//   load() {
//     gsap.fromTo(
//       this.itemsToShow,
//       {y: '0em', autoAlpha: 0},
//       {
//         y: '0em',
//         autoAlpha: 1,
//         duration: durationtFaster,
//         ease: '0.25,0.8,0.25,1',
//         stagger: staggerDefault,
//       }
//     );
//   }

//   reload() {
//     gsap.fromTo(
//       this.itemsToShow,
//       {y: '0em', autoAlpha: 0},
//       {
//         y: '0em',
//         autoAlpha: 1,
//         duration: durationtFaster,
//         ease: '0.25,0.8,0.25,1',
//         stagger: staggerDefault,
//       }
//     );
//   }
// }
// customElements.define('motion-list', MotionList);

/* -------------------------------------------------------------------------- */
/* Menu Toggle */
/* -------------------------------------------------------------------------- */

class MenuToggle extends MagnetButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }

  onClick() {
    this.setAttribute('aria-expanded', this.expanded ? 'false' : 'true');
    if (this.controlledElement) this.controlledElement.classList.toggle('active');
  }
}
customElements.define('menu-toogle', MenuToggle, {extends: 'button'});

/* -------------------------------------------------------------------------- */
/* Custom Select */
/* -------------------------------------------------------------------------- */

class CustomSelect extends HTMLSelectElement {
  constructor() {
    super();

    this.onChange();
    this.addEventListener('change', this.onChange);
  }

  onChange() {
    this.value !== '' || this.selectedIndex === -1 ? this.setAttribute('selected', '') : this.removeAttribute('selected');
  }
}
customElements.define('custom-select', CustomSelect, {extends: 'select'});

/* -------------------------------------------------------------------------- */
/* Buttons/Slider */
/* -------------------------------------------------------------------------- */

class PreviousButton extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  connectedCallback() {
    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:previousStatus', this.updateStatus.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:previousStatus', this.updateStatus.bind(this));
    }
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:previous', {bubbles: true, cancelable: true}));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
        this.hidden = true;
        break;
      case 'disabled':
        this.disabled = true;
        break;
      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('previous-button', PreviousButton, {extends: 'button'});

class NextButton extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick);
  }

  connectedCallback() {
    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:nextStatus', this.updateStatus.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:nextStatus', this.updateStatus.bind(this));
    }
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:next', {bubbles: true, cancelable: true}));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
        this.hidden = true;
        break;
      case 'disabled':
        this.disabled = true;
        break;
      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('next-button', NextButton, {extends: 'button'});

class SliderElement extends HTMLElement {
  constructor() {
    super();
    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              requestAnimationFrame(() => this.init());
              observer.disconnect();
            }
          });
        },
        {rootMargin: '0px 0px 200px 0px'}
      );
      this._observer.observe(this);
    } else {
      requestAnimationFrame(() => this.init());
    }
  }

  get looping() {
    return false;
  }

  get items() {
    this._items = this._items || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
    return this._items;
  }

  get itemsToShow() {
    return Array.from(this.items).filter((element) => element.clientWidth > 0);
  }

  get itemOffset() {
    const items = this.itemsToShow;
    if (items.length < 2) {
      return 0;
    }
    const offset = theme.config.rtl ? items[0].offsetLeft - items[1].offsetLeft : items[1].offsetLeft - items[0].offsetLeft;
    return offset;
  }

  get perPage() {
    let elementWidth = this.clientWidth;
    const mql = window.matchMedia('screen and (min-width: 1280px)');
    if (mql.matches) {
      const styles = window.getComputedStyle(this);
      elementWidth = this.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    }
    const offset = this.itemOffset;
    if (offset <= 0) {
      return 1;
    }
    const calculatedPerPage = Math.floor(elementWidth / offset);
    return calculatedPerPage > 0 ? calculatedPerPage : 1;
  }

  get totalPages() {
    return Math.max(1, this.itemsToShow.length - this.perPage + 1);
  }

  reset() {
    this._items = Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
    this.updateButtons();
  }

  init() {
    this.hasPendingOnScroll = false;
    this.currentPage = 1;
    this.updateButtons();
    this.addEventListener('scroll', theme.utils.debounce(this.update.bind(this), 50));
    this.addEventListener('scrollend', this.scrollend);
    this.addEventListener('slider:previous', this.previous);
    this.addEventListener('slider:next', this.next);
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => event.target.scrollIntoView({behavior: 'smooth'}));
    }
    this.logCurrentSliderState();
  }

  previous() {
    this.scrollPosition = this.scrollLeft - this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  next() {
    this.scrollPosition = this.scrollLeft + this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  select(selectedIndex, immediate = false) {
    this.scrollPosition = this.scrollLeft - (this.currentPage - selectedIndex) * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition, immediate);
  }

  selected(selectedIndex) {
    return this.itemsToShow[selectedIndex];
  }

  scrollend() {
    this.hasPendingOnScroll = false;
    this.dispatchEventHandler();
  }

  update() {
    if (window.onscrollend === void 0) {
      clearTimeout(this.scrollendTimeout);
    }
    const previousPage = this.currentPage;
    this.currentPage = Math.round(Math.abs(this.scrollLeft) / this.itemOffset) + 1;
    if (this.currentPage < 1) this.currentPage = 1;
    if (this.currentPage !== previousPage) {
      if (!this.hasPendingOnScroll) {
        this.dispatchEventHandler();
      }
      this.itemsToShow.forEach((sliderItem, index) => {
        sliderItem.classList.toggle('selected', index + 1 === this.currentPage);
      });
    }
    if (window.onscrollend === void 0) {
      this.scrollendTimeout = setTimeout(() => {
        this.dispatchEvent(new CustomEvent('scrollend', {bubbles: true, composed: true}));
      }, 75);
    }
    if (this.looping) return;
    this.updateButtons();
  }

  updateButtons() {
    let previousDisabled = this.currentPage === 1;
    let nextDisabled = this.currentPage >= this.totalPages;
    if (this.perPage > 1) {
      previousDisabled = previousDisabled || (this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[0]) && this.scrollLeft <= 5);
      nextDisabled = this.scrollLeft + this.clientWidth + 5 >= this.scrollWidth;
    }
    requestAnimationFrame(() => {
      this.dispatchEvent(
        new CustomEvent('slider:previousStatus', {
          bubbles: true,
          detail: {
            status: previousDisabled ? (nextDisabled ? 'hidden' : 'disabled') : 'visible',
          },
        })
      );
      this.dispatchEvent(
        new CustomEvent('slider:nextStatus', {
          bubbles: true,
          detail: {
            status: nextDisabled ? (previousDisabled ? 'hidden' : 'disabled') : 'visible',
          },
        })
      );
    });
  }

  isVisible(element, offset = 0) {
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;
  }

  scrollToPosition(position, immediate = false) {
    this.hasPendingOnScroll = !immediate;
    requestAnimationFrame(() => {
      this.scrollTo({
        left: position,
        behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth',
      });
    });
  }

  dispatchEventHandler() {
    requestAnimationFrame(() => {
      this.dispatchEvent(
        new CustomEvent('slider:change', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.itemsToShow[this.currentPage - 1],
          },
        })
      );
    });
  }

  logCurrentSliderState() {
    console.groupCollapsed('Estado Inicial do Slider para ' + this.id);
    console.log('Total de itens encontrados pelo seletor:', this.items.length);
    console.log('Itens realmente renderizados (clientWidth > 0):', this.itemsToShow.length);
    console.log('Offset de Item Calculado (itemOffset):', this.itemOffset);
    console.log('Itens Por Página Calculados (perPage):', this.perPage);
    console.log('Total de Páginas Calculado (totalPages):', this.totalPages);
    console.log('Largura visível do Slider (clientWidth):', this.clientWidth);
    console.log('Largura total de rolagem (scrollWidth):', this.scrollWidth);
    console.log('Posição de rolagem atual (scrollLeft):', this.scrollLeft);
    console.groupEnd();
  }
}
customElements.define('slider-element', SliderElement);

class SliderDots extends HTMLElement {
  constructor() {
    super();

    new theme.initWhenVisible(() => {
      if (this.controlledElement) {
        this.controlledElement.addEventListener('slider:change', this.onChange.bind(this));

        this.items.forEach((item) => {
          item.addEventListener('click', this.onButtonClick.bind(this));
        });
      }
    });
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return (this._items = this._items || Array.from(this.children));
  }

  get itemsToShow() {
    return Array.from(this.items).filter((element) => element.clientWidth > 0);
  }

  reset() {
    this._items = Array.from(this.children);
  }

  onChange(event) {
    this.transitionTo(parseInt(event.detail.currentPage) - 1);

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(event.detail.currentPage) ? 'true' : 'false');
    });
  }

  transitionTo(selectedIndex, immediate = false) {
    if (this.itemsToShow[selectedIndex]) {
      const scrollElement = this.hasAttribute('align-selected') ? this.closest(this.getAttribute('align-selected')) : this;
      const targetElement = this.itemsToShow[selectedIndex];
      const scrollOptions = {
        left: targetElement.offsetLeft - scrollElement.clientWidth / 2 + targetElement.clientWidth / 2,
        top: targetElement.offsetTop - scrollElement.clientHeight / 2 - targetElement.clientHeight / 2,
        behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth',
      };

      if (!immediate && !theme.config.motionReduced) {
        gsap.to(scrollElement, {
          scrollTo: scrollOptions,
          duration: 0.5,
          ease: '0.25,0.8,0.25,1',
        });
      } else {
        scrollElement.scrollTo(scrollOptions);
      }
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', item.getAttribute('data-index') === target.getAttribute('data-index') ? 'true' : 'false');
    });

    this.controlledElement.select(parseInt(target.getAttribute('data-index')));
  }
}
customElements.define('slider-dots', SliderDots);

/* -------------------------------------------------------------------------- */
/* ProgressBar */
/* -------------------------------------------------------------------------- */

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute('style')) return;

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              requestAnimationFrame(() => this.init());
              observer.disconnect();
            }
          });
        },
        {rootMargin: '0px 0px 200px 0px'}
      );
      this._observer.observe(this);
    } else {
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    const progress = (parseInt(this.getAttribute('data-value')) * 0.75 * 100) / parseInt(this.getAttribute('data-max'));
    requestAnimationFrame(() => {
      gsap.to(this, {
        '--progress': `${progress}%`,
        duration: 1,
        ease: '0.25,0.8,0.25,1',
      });
    });
  }
}
customElements.define('progress-bar', ProgressBar);

/* -------------------------------------------------------------------------- */
/* Media/Video */
/* -------------------------------------------------------------------------- */

let youTubeAPILoaded = false;
const onYouTubePromise = new Promise((resolve) => {
  if (window.YT && window.YT.Player) return resolve();
  window.onYouTubeIframeAPIReady = () => resolve();
  if (!youTubeAPILoaded) {
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.head.appendChild(script);
    youTubeAPILoaded = true;
  }
});

document.body.addEventListener(
  'click',
  (e) => {
    const poster = e.target.closest('[id^="DeferredPoster-"]');
    if (poster) {
      const media = poster.closest('deferred-media, video-media, model-media');
      if (media && typeof media.onPosterClick === 'function') {
        media.onPosterClick(e);
      }
    }
  },
  {passive: true}
);

(function enableAutoplayOnAnyTouch() {
  let autoplayActivated = false;
  function activateAutoplay() {
    if (autoplayActivated) return;
    autoplayActivated = true;
    document.querySelectorAll('video-media[autoplay], deferred-media[autoplay]').forEach((el) => {
      if (typeof el.play === 'function') el.play();
    });
    window.removeEventListener('touchstart', activateAutoplay, true);
    window.removeEventListener('mousedown', activateAutoplay, true);
  }
  window.addEventListener('touchstart', activateAutoplay, true);
  window.addEventListener('mousedown', activateAutoplay, true);
})();

function debounce(fn, wait = 100) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this._paused = false;
    if (this.autoplay) {
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              requestAnimationFrame(() => {
                if (entry.isIntersecting) {
                  if (!this.paused) this.play();
                } else {
                  this.pause();
                }
              });
            });
          },
          {rootMargin: '0px 0px 0px 0px'}
        );
        this._observer.observe(this);
      }
    }
  }

  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]');
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get playing() {
    return this.hasAttribute('playing');
  }

  get paused() {
    return this._paused;
  }

  set paused(val) {
    this._paused = !!val;
  }

  get player() {
    if (!this._playerProxy) {
      this._playerProxy = new Proxy(this.playerTarget(), {
        get: (target, prop) => {
          return async () => {
            target = await target;
            this.playerHandler(target, prop);
          };
        },
      });
    }
    return this._playerProxy;
  }

  static get observedAttributes() {
    return ['playing'];
  }

  onPosterClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.playing) {
      this.paused = true;
      this.pause();
    } else {
      this.paused = false;
      this.play();
    }
  }

  play() {
    if (!this.playing) {
      requestAnimationFrame(() => {
        this.player.play();
      });
    }
  }

  pause() {
    if (this.playing) {
      requestAnimationFrame(() => {
        this.player.pause();
      });
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      if (oldValue === null && newValue === '') {
        requestAnimationFrame(() => {
          this.dispatchEvent(new CustomEvent('video:play', {bubbles: true}));
        });
      }
      if (newValue === null) {
        requestAnimationFrame(() => {
          this.dispatchEvent(new CustomEvent('video:pause', {bubbles: true}));
        });
      }
    }
  }
}

class VideoMedia extends DeferredMedia {
  constructor() {
    super();
  }

  playerTarget() {
    if (this.hasAttribute('host')) {
      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector('template');
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('(max-width: 1023px)').matches;
        if (this.getAttribute('host') === 'youtube') {
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector('iframe'), {
            events: {
              onReady: () => {
                if (muteVideo) player.mute();
                requestAnimationFrame(() => resolve(player));
              },
              onStateChange: (event) => {
                requestAnimationFrame(() => {
                  if (event.data === YT.PlayerState.PLAYING) {
                    this.setAttribute('playing', '');
                  } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                    this.removeAttribute('playing');
                  }
                });
              },
            },
          });
        } else if (this.getAttribute('host') === 'vimeo') {
          if (!window.Vimeo || !window.Vimeo.Player) {
            const script = document.createElement('script');
            script.src = 'https://player.vimeo.com/api/player.js';
            script.async = true;
            document.head.appendChild(script);
            await new Promise((res) => {
              script.onload = res;
            });
          }
          const player = new Vimeo.Player(this.querySelector('iframe'));
          if (muteVideo) player.setMuted(true);
          player.on('play', () => requestAnimationFrame(() => this.setAttribute('playing', '')));
          player.on('pause', () => requestAnimationFrame(() => this.removeAttribute('playing')));
          player.on('ended', () => requestAnimationFrame(() => this.removeAttribute('playing')));
          requestAnimationFrame(() => resolve(player));
        }
      });
    } else {
      this.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');
      const player = this.querySelector('video');
      player.addEventListener('play', () => {
        requestAnimationFrame(() => {
          this.setAttribute('playing', '');
          this.removeAttribute('suspended');
        });
      });
      player.addEventListener('pause', () => {
        requestAnimationFrame(() => {
          if (!player.seeking && player.paused) {
            this.removeAttribute('playing');
          }
        });
      });
      return player;
    }
  }

  playerHandler(target, prop) {
    if (this.getAttribute('host') === 'youtube') {
      requestAnimationFrame(() => {
        prop === 'play' ? target.playVideo() : target.pauseVideo();
      });
    } else {
      if (prop === 'play' && !this.hasAttribute('host')) {
        target.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            requestAnimationFrame(() => {
              this.setAttribute('suspended', '');
              target.controls = true;
              const replacementImageSrc = target.previousElementSibling?.currentSrc;
              if (replacementImageSrc) {
                target.poster = replacementImageSrc;
              }
            });
          }
        });
      } else {
        requestAnimationFrame(() => {
          target[prop]();
        });
      }
    }
  }
}
customElements.define('video-media', VideoMedia);

// class ModelMedia extends DeferredMedia {
//   constructor() {
//     super();
//     this.player;
//     if (this.closeElement) {
//       this.closeElement.addEventListener('click', this.onCloseClick.bind(this));
//     }
//   }

//   get closeElement() {
//     return this.querySelector('[id^="DeferredPosterClose-"]');
//   }

//   onCloseClick(event) {
//     event.preventDefault();
//     event.stopPropagation();
//     if (this.modelViewerUI) {
//       this.modelViewerUI.pause();
//     }
//   }

//   playerTarget() {
//     return new Promise(() => {
//       this.setAttribute('loaded', '');
//       Shopify.loadFeatures([
//         {
//           name: 'shopify-xr',
//           version: '1.0',
//           onLoad: this.setupShopifyXR.bind(this),
//         },
//         {
//           name: 'model-viewer-ui',
//           version: '1.0',
//           onLoad: this.setupModelViewerUI.bind(this),
//         },
//       ]);
//     });
//   }

//   playerHandler(target, prop) {
//     target[prop]();
//   }

//   async setupShopifyXR() {
//     if (!window.ShopifyXR) {
//       document.addEventListener('shopify_xr_initialized', this.setupShopifyXR.bind(this), {once: true});
//       return;
//     }
//     document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
//       window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
//       modelJSON.remove();
//     });
//     window.ShopifyXR.setupXRElements();
//   }

//   setupModelViewerUI(errors) {
//     if (errors) return;
//     const modelViewer = this.querySelector('model-viewer');
//     if (modelViewer && !modelViewer.hasAttribute('loaded')) {
//       modelViewer.setAttribute('loaded', '');
//       modelViewer.addEventListener('shopify_model_viewer_ui_toggle_play', this.modelViewerPlayed.bind(this));
//       modelViewer.addEventListener('shopify_model_viewer_ui_toggle_pause', this.modelViewerPaused.bind(this));
//       this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer);
//     }
//   }

//   modelViewerPlayed() {
//     this.setAttribute('playing', '');
//     this.closeElement?.removeAttribute('hidden');
//     (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:play', {bubbles: true}));
//   }

//   modelViewerPaused() {
//     this.removeAttribute('playing');
//     this.closeElement?.setAttribute('hidden', '');
//     (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:pause', {bubbles: true}));
//   }
// }
// customElements.define('model-media', ModelMedia);

/* -------------------------------------------------------------------------- */
/* Variants */
/* -------------------------------------------------------------------------- */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event.target);

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata(target) {
    if (target.tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.hasAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', '');
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map((selector) => selector.getAttribute('data-option-value-id'));
  }
}
customElements.define('variant-selects', VariantSelects);

/* -------------------------------------------------------------------------- */
/* Product Info */
/* -------------------------------------------------------------------------- */

class ProductInfo extends HTMLElement {
  onVariantChangeUnsubscriber = undefined;
  cartUpdateUnsubscriber = undefined;
  abortController = undefined;
  pendingRequestUrl = null;
  preProcessHtmlCallbacks = [];
  postProcessHtmlCallbacks = [];

  constructor() {
    super();
  }

  get sectionId() {
    return this.hasAttribute('data-original-section-id') ? this.getAttribute('data-original-section-id') : this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get productForm() {
    return this.querySelector('form[is="product-form"]');
  }

  get productStickyForm() {
    return document.getElementById(`ProductStickyForm-${this.sectionId}-${this.productId}`);
  }

  get pickupAvailability() {
    return this.querySelector('pickup-availability');
  }

  get variantSelectors() {
    return this.querySelector('variant-selects');
  }

  get quantityInput() {
    return this.querySelector('quantity-input input');
  }

  connectedCallback() {
    this.onVariantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueChange.bind(this));

    this.initQuantityHandlers();
    this.dispatchEvent(new CustomEvent('product-info:loaded', {bubbles: true}));
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
    this.cartUpdateUnsubscriber?.();
  }

  handleOptionValueChange({data: {event, target, selectedOptionValues}}) {
    if (!this.contains(event.target)) return;

    this.resetProductFormState();

    const productUrl = target.getAttribute('data-product-url') || this.pendingRequestUrl || this.getAttribute('data-product-url');
    this.pendingRequestUrl = productUrl;
    const shouldSwapProduct = this.getAttribute('data-product-url') !== productUrl;

    if (shouldSwapProduct) {
      window.location.href = productUrl;
      return;
    }

    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
      targetId: target.tagName === 'OPTION' ? target.parentElement.id : target.id,
      callback: this.handleUpdateProductInfo(),
    });
  }

  resetProductFormState() {
    this.productForm?.handleErrorMessage();
    this.productForm?.toggleSubmitButton(true, '');
    this.productStickyForm?.handleErrorMessage();
    this.productStickyForm?.toggleSubmitButton(true, '');
  }

  renderProductInfo({requestUrl, targetId, callback}) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, {signal: this.abortController.signal})
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        if (callback !== undefined) {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          callback(parsedHTML);
        }
      })
      .then(() => {
        // set focus to last clicked option value
        const activeElement = document.getElementById(targetId);
        if (activeElement === null) return;

        if (activeElement.hasAttribute('align-selected')) {
          const scrollElement = activeElement.closest(activeElement.getAttribute('align-selected'));
          scrollElement.scrollTo({
            left: activeElement.offsetLeft,
            behavior: 'instant',
          });
        }
        setTimeout(() => {
          activeElement.focus({preventScroll: true});
        }, 100);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];
    params.push(`section_id=${this.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  handleUpdateProductInfo() {
    return (parsedHTML) => {
      const variant = this.getSelectedVariant(parsedHTML);

      this.pickupAvailability?.update(variant);
      this.updateOptionValues(parsedHTML);
      this.updateURL(variant?.id);
      this.updateVariantInputs(variant?.id);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateMedia(parsedHTML, variant?.featured_media?.id);

      const updateSourceFromDestination = (id) => {
        const source = parsedHTML.getElementById(`${id}-${this.sectionId}-${this.productId}`);
        const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`);
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
          destination.removeAttribute('hidden');
        }
      };

      updateSourceFromDestination('Price');
      updateSourceFromDestination('StickyPrice');
      updateSourceFromDestination('Sku');
      updateSourceFromDestination('Inventory');
      updateSourceFromDestination('Volume');
      updateSourceFromDestination('PricePerItem');
      updateSourceFromDestination('BackInStock');

      this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
      updateSourceFromDestination('QuantityRules');
      updateSourceFromDestination('QuantityRulesCart');
      updateSourceFromDestination('VolumeNote');

      this.productForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);
      this.productStickyForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          productId: this.productId,
          parsedHTML: parsedHTML,
          variant: variant,
        },
      });
      (this.productForm ?? document).dispatchEvent(
        new CustomEvent('variant:change', {
          detail: {
            variant: variant,
          },
        })
      );
    };
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('[data-selected-variant]')?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  updateOptionValues(parsedHTML) {
    const variantSelects = parsedHTML.getElementById(`VariantPicker-${this.sectionId}-${this.productId}`);
    if (variantSelects) {
      theme.HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
    }
  }

  updateURL(variantId) {
    if (!variantId || this.getAttribute('data-update-url') === 'false') return;

    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('variant', variantId);
    window.history.replaceState({path: newUrl.toString()}, '', newUrl.toString());
  }

  updateVariantInputs(variantId) {
    if (!variantId) return;

    const productForms = document.querySelectorAll(`#ProductForm-${this.sectionId}-${this.productId}, #ProductFormInstallment-${this.sectionId}-${this.productId}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variantId ?? '';
      input.dispatchEvent(new Event('change', {bubbles: true}));
    });
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true);
    this.productStickyForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true);

    const selectors = ['Price', 'StickyPrice', 'Inventory', 'Sku', 'PricePerItem', 'BackInStock', 'VolumeNote', 'Volume', 'QuantityRules', 'QuantityRulesCart'].map((id) => `#${id}-${this.sectionId}-${this.productId}`).join(', ');
    document.querySelectorAll(selectors).forEach((selector) => selector.setAttribute('hidden', ''));
  }

  initQuantityHandlers() {
    if (!this.quantityInput) return;

    this.setQuantityBoundries();
    if (!this.hasAttribute('data-original-section-id')) {
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
    }
  }

  setQuantityBoundries() {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId,
      },
    });
  }

  fetchQuantityRules() {
    const currentVariantId = this.productForm?.variantIdInput?.value;
    if (!currentVariantId) return;

    this.querySelector('label[is="quantity-label"]')?.setAttribute('aria-busy', 'true');
    fetch(`${this.getAttribute('data-product-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.querySelector('label[is="quantity-label"]')?.removeAttribute('aria-busy');
      });
  }

  updateQuantityRules(sectionId, productId, parsedHTML) {
    if (!this.quantityInput) return;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityRules, {
      data: {
        sectionId,
        productId,
        parsedHTML,
      },
    });

    this.setQuantityBoundries();
  }

  updateMedia() {
    // todo
  }
}
customElements.define('product-info', ProductInfo);

class ProductForm extends HTMLFormElement {
  constructor() {
    super();

    this.variantIdInput.disabled = false;
    this.addEventListener('submit', this.onSubmitHandler);
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get submitButton() {
    return (this._submitButton = this._submitButton || this.querySelector('[type="submit"]'));
  }

  get submitButtons() {
    return (this._submitButtons = this._submitButtons || document.querySelectorAll(`[type="submit"][form="${this.getAttribute('id')}"]`));
  }

  get hideErrors() {
    return this.getAttribute('data-hide-errors') === 'true';
  }

  get variantIdInput() {
    return this.querySelector('[name="id"]');
  }

  onSubmitHandler(event) {
    if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') return;

    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', {
        bubbles: true,
        detail: {sections: sectionsToBundle},
      })
    );

    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this);
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);

    config.body = formData;

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message,
          });
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', {
              detail: {
                errorMessage: parsedState.description,
              },
            })
          );

          const submitButtonText = this.submitButton.querySelector('.btn-text span');
          if (!submitButtonText || !submitButtonText.hasAttribute('data-sold-out')) return;
          submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.error = true;
          return;
        }

        const cartJson = await (
          await fetch(`${theme.routes.cart_url}`, {
            ...theme.utils.fetchConfig(),
          })
        ).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          cart: cartJson,
        });
        document.dispatchEvent(
          new CustomEvent('ajaxProduct:added', {
            detail: {
              product: parsedState,
            },
          })
        );

        const quickViewModal = this.closest('quick-view');
        if (quickViewModal) {
          document.body.addEventListener(
            'modal:afterHide',
            () => {
              setTimeout(() => {
                this.cartDrawer?.show(this.activeElement);
              });
            },
            {once: true}
          );
          quickViewModal.hide(true);
        } else {
          this.cartDrawer?.show(this.activeElement);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');
        this.submitButtons.forEach((submitButton) => submitButton.removeAttribute('aria-busy'));

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtons.forEach((submitButton) => submitButton.removeAttribute('aria-disabled'));
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text, unavailable = false) {
    if (!this.submitButton) return;

    this.submitButton.removeAttribute('loading');
    this.submitButton.removeAttribute('unavailable');

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (unavailable) this.submitButton.setAttribute('unavailable', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      } else {
        this.submitButton.setAttribute('loading', '');
      }
    } else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = this.submitButton.getAttribute('data-pre-order') === 'true' ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
    }
  }
}
customElements.define('product-form', ProductForm, {extends: 'form'});

class ProductStickyForm extends HTMLElement {
  constructor() {
    super();

    this.scopeFrom = document.getElementById(this.getAttribute('form'));
    this.scopeTo = document.querySelector('.footer-group');

    if (!this.scopeFrom || !this.scopeTo) {
      return;
    }

    const intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this));
    intersectionObserver.observe(this.scopeFrom);
    intersectionObserver.observe(this.scopeTo);

    this.onVariantChangedListener = this.onVariantChanged.bind(this);
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get submitButton() {
    return (this._submitButton = this._submitButton || this.querySelector('[type="submit"]'));
  }

  get productOptions() {
    return (this._productOptions = this._productOptions || this.querySelector('[data-sticky-product-options]'));
  }

  get productMedia() {
    return (this._productMedia = this._productMedia || this.querySelector('[data-sticky-product-media]'));
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this.scopeFrom) {
        this.scopeFromPassed = entry.boundingClientRect.bottom < 0;
      }
      if (entry.target === this.scopeTo) {
        this.scopeToReached = entry.isIntersecting;
      }
    });

    if (this.scopeFromPassed && !this.scopeToReached) {
      gsap.to(this.firstElementChild, {
        opacity: 1,
        visibility: 'visible',
        y: 0,
        duration: 1,
        ease: '0.25,0.8,0.25,1',
      });
    } else {
      gsap.to(this.firstElementChild, {
        opacity: 0,
        visibility: 'hidden',
        y: 15,
        duration: 1,
        ease: '0.25,0.8,0.25,1',
      });
    }
  }

  connectedCallback() {
    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChangedListener);
  }

  disconnectedCallback() {
    (this.productForm ?? document).removeEventListener('variant:change', this.onVariantChangedListener);
  }

  onVariantChanged(event) {
    this.updateProductMedia(event.detail.variant);
    this.updateProductOptions(event.detail.variant);
  }

  updateProductMedia(currentVariant) {
    if (!currentVariant || !currentVariant.featured_media) return;

    const image = this.productMedia.querySelector('img');
    const newImage = new Image(currentVariant.featured_media.preview_image.width, currentVariant.featured_media.preview_image.height);

    newImage.alt = currentVariant.featured_media.alt;
    newImage.src = currentVariant.featured_media.preview_image.src;
    newImage.srcset = this.generateSrcset(currentVariant.featured_media.preview_image);
    newImage.sizes = image.sizes;

    this.productMedia.replaceChildren(newImage);
  }

  updateProductOptions(currentVariant) {
    if (!currentVariant) return;

    this.productOptions.innerText = currentVariant.title;
  }

  handleErrorMessage(errorMessage = false) {
    if (this.productForm?.hideErrors) return;

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return;

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
    } else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = this.submitButton.getAttribute('data-pre-order') === 'true' ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
    }
  }

  generateSrcset(image) {
    const widths = this.productMedia
      .getAttribute('data-widths')
      .split(',')
      .map((width) => parseInt(width));
    return widths
      .filter((width) => width <= image.width)
      .map((width) => {
        return `${image.src}&width=${width} ${width}w`;
      })
      .join(', ');
  }
}
customElements.define('product-sticky-form', ProductStickyForm);

/* -------------------------------------------------------------------------- */
/* Media Gallery */
/* -------------------------------------------------------------------------- */

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              requestAnimationFrame(() => this.pauseAllMedia());
              observer.disconnect();
            }
          });
        },
        {rootMargin: '0px 0px 0px 0px'}
      );
      this._observer.observe(this);
    } else {
      requestAnimationFrame(() => this.pauseAllMedia());
    }

    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChanged.bind(this));

    this.addEventListener('lightbox:open', (event) => this.openZoom(event.detail.index));
    this.sliderGallery.addEventListener('slider:change', this.onSlideChange.bind(this));

    this.countMediaGallery();
  }

  get viewInSpaceButton() {
    return this.querySelector('[data-shopify-xr]');
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get sliderGallery() {
    return this.querySelector('slider-element');
  }

  get sliderDots() {
    return this.querySelector('media-dots');
  }

  get mediaPreview() {
    return this.querySelector('.product__preview .product__media');
  }

  get hideVariants() {
    return (this._hideVariants = this._hideVariants || this.querySelectorAll('.product__media--variant').length > 0);
  }

  get gangOption() {
    if (this._gangOption) return this._gangOption;
    const mediaItemWithGang = this.sliderGallery.querySelector('[data-gang-option]');
    return mediaItemWithGang ? (this._gangOption = mediaItemWithGang.getAttribute('data-gang-option')) : false;
  }

  onVariantChanged(event) {
    const currentVariant = event.detail.variant;
    if (!currentVariant.featured_media) return;

    const newMedia = this.sliderGallery.querySelector(`[data-media-id="${currentVariant.featured_media.id}"]`);
    if (newMedia === null) return;

    if (this.gangOption) {
      this.sliderGallery.items.forEach((item) => (item.hidden = item.getAttribute('data-gang-connect') !== newMedia.getAttribute('data-gang-connect')));
      this.sliderGallery.reset();

      if (this.sliderDots) {
        this.sliderDots.items.forEach((item) => (item.hidden = item.getAttribute('data-gang-connect') !== newMedia.getAttribute('data-gang-connect')));
        this.sliderDots.reset();
        this.sliderDots.resetIndexes();
        this.sliderDots.transitionTo(1, true);
      }
    }

    this.setActiveMedia(currentVariant.featured_media.id, this.hideVariants);

    if (this.mediaPreview) {
      this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media) => media.classList.remove('xl:hidden'));
      this.mediaPreview.parentNode.replaceChild(newMedia.cloneNode(true), this.mediaPreview);
      newMedia.classList.add('xl:hidden');
    }

    this.countMediaGallery();
  }

  onSlideChange(event) {
    const activeMedia = event.detail.currentElement;
    this.playActiveMedia(activeMedia);

    if (this.viewInSpaceButton) {
      if (activeMedia.getAttribute('data-media-type') === 'model') {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', activeMedia.getAttribute('data-media-id'));
      } else {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', this.viewInSpaceButton.getAttribute('data-shopify-model3d-default-id'));
      }
    }
  }

  setActiveMedia(mediaId, prepend) {
    const activeMedia = this.sliderGallery.querySelector(`[data-media-id="${mediaId}"]`);

    if (prepend) {
      activeMedia.parentElement.prepend(activeMedia);
      this.sliderGallery.reset();

      if (this.sliderDots) {
        const activeThumbnail = this.sliderDots.querySelector(`[data-media-id="${mediaId}"]`);
        activeThumbnail.parentElement.prepend(activeThumbnail);
        this.sliderDots.reset();
        this.sliderDots.resetIndexes();
      }
    } else {
      this.sliderGallery.select(this.sliderGallery.itemsToShow.indexOf(activeMedia) + 1, true);
    }

    if (this.gangOption) {
      this.sliderGallery.select(1, true);
    }

    if (theme.config.mqlSmall) {
      const quickViewModal = this.closest('quick-view');
      if (quickViewModal) {
        quickViewModal.querySelector('.quick-view__content').scrollTo({
          top: activeMedia.getBoundingClientRect().top,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth',
        });
      } else {
        window.scrollTo({
          top: activeMedia.getBoundingClientRect().top + window.scrollY - 95,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth',
        });
      }
    }
  }

  playActiveMedia(activeMedia) {
    if (typeof activeMedia === 'undefined') return;

    const deferredMedia = activeMedia.querySelector('.deferred-media');

    this.sliderGallery.querySelectorAll('.deferred-media').forEach((media) => {
      deferredMedia === media ? media.play() : media.pause();
    });
  }

  pauseAllMedia() {
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media, index) => {
      if (index > 0) {
        const deferredMedia = media.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.pause === 'function') deferredMedia.pause();
      }
    });
  }

  countMediaGallery() {
    let mediaCount = 0;
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media) => {
      if (!media.classList.contains('xl:hidden') && !media.hasAttribute('hidden')) {
        mediaCount++;
      }
    });

    if (this.mediaPreview) {
      mediaCount++;
    }

    mediaCount > 1 ? this.classList.remove('with-only1') : this.classList.add('with-only1');
  }
}
customElements.define('media-gallery', MediaGallery);

/* -------------------------------------------------------------------------- */
/* Modal */
/* -------------------------------------------------------------------------- */

class XModal extends ModalElement {
  constructor() {
    super();
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('x-modal', XModal);

/* -------------------------------------------------------------------------- */
/* Tabs Element */
/* -------------------------------------------------------------------------- */

// class TabsElement extends HTMLElement {
//   constructor() {
//     super();

//     this.selectedIndex = this.selectedIndex;
//     this.buttons.forEach((button, index) => button.addEventListener('click', () => (this.selectedIndex = index)));

//     if (Shopify.designMode) {
//       this.addEventListener('shopify:block:select', (event) => (this.selectedIndex = this.buttons.indexOf(event.target)));
//     }
//   }

//   static get observedAttributes() {
//     return ['selected-index'];
//   }

//   get selectedIndex() {
//     return parseInt(this.getAttribute('selected-index')) || 0;
//   }

//   set selectedIndex(index) {
//     this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
//   }

//   get buttons() {
//     return (this._buttons = this._buttons || Array.from(this.querySelectorAll('button[role="tab"]')));
//   }

//   get indicators() {
//     return (this._indicators = this._indicators || Array.from(this.querySelectorAll('.indicators')));
//   }

//   attributeChangedCallback(name, oldValue, newValue) {
//     this.buttons.forEach((button, index) => {
//       button.classList.toggle('button--primary', index === parseInt(newValue));
//       button.classList.toggle('button--secondary', index !== parseInt(newValue));
//       button.disabled = index === parseInt(newValue);
//     });

//     this.indicators.forEach((button, index) => {
//       button.hidden = index !== parseInt(newValue);
//     });

//     if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
//       const fromButton = this.buttons[parseInt(oldValue)];
//       const toButton = this.buttons[parseInt(newValue)];
//       this.transition(document.getElementById(fromButton.getAttribute('aria-controls')), document.getElementById(toButton.getAttribute('aria-controls')));
//     }
//   }

//   async transition(fromPanel, toPanel) {
//     await Motion.animate(fromPanel, {transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0]}, {duration: 0.15}).finished;

//     fromPanel.hidden = true;
//     toPanel.hidden = false;

//     Motion.animate(toPanel, {transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1]}, {duration: 0.15}).finished;
//     toPanel.querySelector('motion-list')?.load();
//   }
// }
// customElements.define('tabs-element', TabsElement);

/* -------------------------------------------------------------------------- */
/* Slideshow */
/* -------------------------------------------------------------------------- */

// class SlideshowElement extends HTMLElement {
//   constructor() {
//     super();

//     this.selectedIndex = this.selectedIndex;

//     if (theme.config.isTouch) {
//       new theme.initWhenVisible(this.init.bind(this));
//     } else {
//       gsap.utils.toArray(this).forEach((element) => {
//         ScrollTrigger.create({
//           trigger: element,
//           start: 'top bottom+=200',
//           onEnter: this.init.bind(this),
//         });
//       });
//     }
//   }

//   static get observedAttributes() {
//     return ['selected-index'];
//   }

//   get selectedIndex() {
//     return parseInt(this.getAttribute('selected-index')) || 0;
//   }

//   set selectedIndex(index) {
//     this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
//   }

//   get items() {
//     return (this._items = this._items || Array.from(this.children));
//   }

//   get autoplay() {
//     return this.hasAttribute('autoplay');
//   }

//   get speed() {
//     return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
//   }

//   get adaptiveHeightProperty() {
//     return false;
//   }

//   init() {
//     const that = this;
//     if (this.items.length > 1) {
//       this.slider = new Flickity(this, {
//         accessibility: false,
//         pageDots: false,
//         prevNextButtons: false,
//         wrapAround: true,
//         rightToLeft: theme.config.rtl,
//         autoPlay: this.autoplay ? this.speed : false,
//         adaptiveHeightProperty: this.adaptiveHeightProperty,
//         on: {
//           ready: function () {
//             const {selectedElement} = this;
//             that.onReady(selectedElement);
//           },
//         },
//       });

//       this.slider.on('change', this.onChange.bind(this));
//       this.addEventListener('slider:previous', () => this.slider.previous());
//       this.addEventListener('slider:next', () => this.slider.next());
//       this.addEventListener('slider:play', () => this.slider.playPlayer());
//       this.addEventListener('slider:pause', () => this.slider.pausePlayer());

//       if (Shopify.designMode) {
//         this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)));
//       }
//     } else if (this.items.length === 1) {
//       const selectedElement = this.firstChild;
//       that.onReady(selectedElement);
//     }
//   }

//   disconnectedCallback() {
//     if (this.slider) this.slider.destroy();
//   }

//   attributeChangedCallback(name, oldValue, newValue) {
//     if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
//       const fromElement = this.items[parseInt(oldValue)];
//       const toElement = this.items[parseInt(newValue)];

//       const animateElement = toElement.querySelector('animate-element');
//       animateElement?.refresh();

//       if (fromElement.getAttribute('data-type') === 'video') {
//         const fromVideoElement = theme.utils.visibleMedia(fromElement.querySelectorAll('video-media'));
//         fromVideoElement?.pause();
//       }

//       if (toElement.getAttribute('data-type') === 'video') {
//         const toVideoElement = theme.utils.visibleMedia(toElement.querySelectorAll('video-media'));
//         toVideoElement?.play();
//       }
//     }
//   }

//   onChange() {
//     this.selectedIndex = this.slider.selectedIndex;
//     this.dispatchEvent(
//       new CustomEvent('slider:change', {
//         bubbles: true,
//         detail: {currentPage: this.slider.selectedIndex},
//       })
//     );
//   }

//   select(selectedIndex) {
//     this.slider.select(selectedIndex);
//   }

//   onReady(selectedElement) {
//     if (selectedElement.getAttribute('data-type') === 'video') {
//       const videoElement = theme.utils.visibleMedia(selectedElement.querySelectorAll('video-media'));
//       videoElement?.play();
//     }

//     if (!theme.config.isTouch) {
//       const animateElement = selectedElement.querySelector('animate-element');
//       animateElement?.refresh();
//     }
//   }
// }
// customElements.define('slideshow-element', SlideshowElement);

// class SlideshowHero extends SlideshowElement {
//   constructor() {
//     super();

//     window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), false);
//   }

//   get adaptiveHeightProperty() {
//     return true;
//   }

//   get heroSection() {
//     return this.closest('.hero-section');
//   }

//   get nextSection() {
//     let nextSibling = this.heroSection.nextSibling;
//     for (let i = 0; i < 20; i++) {
//       if (nextSibling.classList.contains('shopify-section')) break;
//       nextSibling = nextSibling.nextSibling;
//     }

//     if (nextSibling && nextSibling.nodeType != 1) {
//       nextSibling = document.getElementById('Layout');
//     }

//     return nextSibling;
//   }

//   onScrollHandler() {
//     const bounding = this.nextSection.getBoundingClientRect();
//     this.heroSection.classList.toggle('hero-scrolled', bounding.y < -30);
//   }
// }
// customElements.define('slideshow-hero', SlideshowHero);

// class SlideshowWords extends HTMLElement {
//   constructor() {
//     super();

//     this.selectedIndex = this.selectedIndex;

//     if (this.controlledElement) {
//       this.controlledElement.addEventListener('slider:change', (event) => (this.selectedIndex = event.detail.currentPage));
//     }
//   }

//   static get observedAttributes() {
//     return ['selected-index'];
//   }

//   get selectedIndex() {
//     return parseInt(this.getAttribute('selected-index')) || 0;
//   }

//   set selectedIndex(index) {
//     this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
//   }

//   get controlledElement() {
//     return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
//   }

//   get items() {
//     return (this._items = this._items || Array.from(this.children));
//   }

//   attributeChangedCallback(name, oldValue, newValue) {
//     if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
//       this.transition(this.items[parseInt(oldValue)], this.items[parseInt(newValue)]);
//     }
//   }

//   transition(fromElement, toElement) {
//     const fromWords = Array.from(fromElement.querySelectorAll('animate-element'));
//     const toWords = Array.from(toElement.querySelectorAll('animate-element'));

//     fromWords.forEach((element) => element.reset());

//     setTimeout(
//       () => {
//         this.items.forEach((item) => {
//           item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(this.selectedIndex) ? 'true' : 'false');
//         });

//         toWords.forEach((element) => element.refresh());
//       },
//       500 + 30 * fromWords.length
//     );
//   }
// }
// customElements.define('slideshow-words', SlideshowWords);

// class ControlButton extends HTMLButtonElement {
//   constructor() {
//     super();

//     this.addEventListener('click', this.onClick);
//   }

//   get controlledElement() {
//     return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
//   }

//   get paused() {
//     return this.hasAttribute('paused');
//   }

//   static get observedAttributes() {
//     return ['paused'];
//   }

//   attributeChangedCallback(name, oldValue, newValue) {
//     if (name === 'paused') {
//       if (oldValue === null && newValue === '') {
//         return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:pause', {bubbles: true}));
//       }

//       if (newValue === null) {
//         return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:play', {bubbles: true}));
//       }
//     }
//   }

//   onClick() {
//     this.paused ? this.removeAttribute('paused') : this.setAttribute('paused', '');
//   }
// }
// customElements.define('control-button', ControlButton, {extends: 'button'});

/* -------------------------------------------------------------------------- */
/* Filters */
/* -------------------------------------------------------------------------- */

// class FiltersToggle extends HoverButton {
//   constructor() {
//     super();

//     this.addEventListener('click', this.onClick);

//     if (theme.config.hasLocalStorage) {
//       const expanded = window.localStorage.getItem(`${theme.settings.themeName}:filters-toogle`) || 'false';
//       this.onToggle(expanded === 'true');
//     }
//   }

//   get controlledElement() {
//     return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
//   }

//   get expanded() {
//     return this.getAttribute('aria-expanded') === 'true';
//   }

//   onClick() {
//     this.onToggle(this.expanded);
//   }

//   onToggle(expanded) {
//     this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
//     if (this.controlledElement) this.controlledElement.classList.toggle('xl:block', !expanded);

//     if (theme.config.hasLocalStorage) {
//       window.localStorage.setItem(`${theme.settings.themeName}:filters-toogle`, expanded);
//     }
//   }
// }
// customElements.define('filters-toogle', FiltersToggle, {extends: 'button'});

/* -------------------------------------------------------------------------- */
/* Section What Is */
/* -------------------------------------------------------------------------- */

class WhatIsImageHover {
  constructor(root) {
    this.root = root;
    this.items = Array.from(root.querySelectorAll('button.what-is__item'));
    this.slides = Array.from(root.querySelectorAll('.what-is__slide'));
    this.slideDuration = 5000;
    this.animationDuration = 0.735;
    this.interval = null;
    this.hovering = false;
    this.currentIndex = this.items.findIndex((item) => item.classList.contains('is-active'));
    if (this.currentIndex < 0) this.currentIndex = 0;
    this.isAnimating = false;
    this.debounceTimer = null;

    this.slides.forEach((slide, i) => {
      requestAnimationFrame(() => {
        gsap.set(slide, {
          scale: i === this.currentIndex ? 1 : 1.1,
          autoAlpha: i === this.currentIndex ? 1 : 0,
          transformOrigin: 'center center',
        });
      });
    });

    this.items.forEach((item, idx) => {
      item.addEventListener('mouseenter', () => this.debounceHover(idx, true));
      item.addEventListener('focus', () => this.debounceHover(idx, true));
      item.addEventListener('mouseleave', () => this.debounceHover(idx, false));
      item.addEventListener('blur', () => this.debounceHover(idx, false));
      item.addEventListener('click', () => {
        this.stopAuto();
        this.activate(idx);
      });
    });

    this.activate(this.currentIndex, true);
    if (this.items.length > 1) {
      this.startAuto();
    }
  }

  debounceHover(idx, hover) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.onHover(idx, hover);
    }, 50);
  }

  onHover(idx, hover) {
    this.hovering = hover;
    if (hover) {
      this.stopAuto();
      if (!this.isAnimating && idx !== this.currentIndex) {
        this.activate(idx);
      }
    } else {
      this.startAuto();
    }
  }

  startAuto() {
    if (this.interval || this.items.length <= 1) return;
    this.interval = setInterval(() => {
      if (!this.hovering && !this.isAnimating) {
        this.activate((this.currentIndex + 1) % this.items.length);
      }
    }, this.slideDuration);
  }

  stopAuto() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  activate(idx, immediate = false) {
    if (!immediate && (this.isAnimating || idx === this.currentIndex)) {
      return;
    }

    if (immediate) {
      requestAnimationFrame(() => {
        gsap.killTweensOf(this.slides);
        gsap.killTweensOf(this.items.map((item) => item.querySelector('.link-underline')));

        this.slides.forEach((slide, i) => {
          const isActive = i === idx;
          slide.classList.toggle('is-active', isActive);
          gsap.set(slide, {
            scale: isActive ? 1 : 1.1,
            autoAlpha: isActive ? 1 : 0,
            transformOrigin: 'center center',
          });
        });

        this.items.forEach((item, i) => {
          const underline = item.querySelector('.link-underline');
          const isActive = i === idx;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-selected', isActive.toString());
          if (underline) {
            gsap.set(underline, {'--underline-progress': isActive ? 1 : 0});
          }
        });

        this.currentIndex = idx;
        this.isAnimating = false;
      });
      return;
    }

    this.isAnimating = true;

    const outgoingSlide = this.slides[this.currentIndex];
    const incomingSlide = this.slides[idx];

    requestAnimationFrame(() => {
      const timeline = gsap.timeline({
        defaults: {
          ease: '0.25,0.8,0.25,1',
        },
        onComplete: () => {
          if (outgoingSlide) {
            outgoingSlide.classList.remove('is-active');
          }
          this.currentIndex = idx;
          this.isAnimating = false;
        },
      });

      if (incomingSlide) {
        incomingSlide.classList.add('is-active');
        gsap.set(incomingSlide, {
          scale: 1.1,
          autoAlpha: 0,
          transformOrigin: 'center center',
        });
      }

      if (outgoingSlide) {
        timeline.to(
          outgoingSlide,
          {
            scale: 1.1,
            autoAlpha: 0,
            duration: this.animationDuration,
            ease: '0.25,0.8,0.25,1',
          },
          0
        );
      }

      if (incomingSlide) {
        timeline.to(
          incomingSlide,
          {
            scale: 1,
            autoAlpha: 1,
            duration: this.animationDuration,
            ease: '0.25,0.8,0.25,1',
          },
          outgoingSlide ? '<0.2' : 0
        );
      }

      this.items.forEach((item, i) => {
        const underline = item.querySelector('.link-underline');
        const isActive = i === idx;

        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive.toString());

        if (underline) {
          gsap.killTweensOf(underline);
          if (isActive) {
            gsap.to(underline, {
              '--underline-progress': 1,
              duration: this.animationDuration,
              ease: '0.25,0.8,0.25,1',
            });
          } else {
            gsap.set(underline, {'--underline-progress': 0});
          }
        }
      });
    });
  }
}

const initWhatIs = (section) => {
  if (section.querySelector('.what-is__slide') && section.querySelectorAll('button.what-is__item').length > 0) {
    requestAnimationFrame(() => {
      new WhatIsImageHover(section);
    });
  }
};

function observeWhatIsSection(section) {
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries, observerInstance) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            requestAnimationFrame(() => {
              initWhatIs(entry.target);
            });
            observerInstance.disconnect();
          }
        });
      },
      {rootMargin: '0px 0px 200px 0px'}
    );
    observer.observe(section);
  } else {
    requestAnimationFrame(() => {
      initWhatIs(section);
    });
  }
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.matches('.what-is-img')) {
      observeWhatIsSection(e.target);
    }
  });
}

document.querySelectorAll('.what-is-img').forEach(observeWhatIsSection);
